import {
  pgTable,
  index,
  bigint,
  varchar,
  text,
  json,
  char,
  timestamp,
  uniqueIndex,
  uuid,
  boolean,
  jsonb,
  integer,
  unique,
  doublePrecision,
  foreignKey,
  date,
  time,
  numeric,
  smallint,
  primaryKey,
  check,
  type AnyPgColumn,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";

export const activity_log = pgTable(
  "activity_log",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "activity_log_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    log_name: varchar({ length: 255 }),
    description: text().notNull(),
    subject_type: varchar({ length: 255 }),
    event: varchar({ length: 255 }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    subject_id: bigint({ mode: "number" }),
    causer_type: varchar({ length: 255 }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    causer_id: bigint({ mode: "number" }),
    properties: json(),
    batch_uuid: char({ length: 36 }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("activity_log_causer_type_causer_id_idx").using(
      "btree",
      table.causer_type.asc().nullsLast().op("int8_ops"),
      table.causer_id.asc().nullsLast().op("int8_ops"),
    ),
    index("activity_log_log_name_idx").using(
      "btree",
      table.log_name.asc().nullsLast().op("text_ops"),
    ),
    index("activity_log_subject_type_subject_id_idx").using(
      "btree",
      table.subject_type.asc().nullsLast().op("int8_ops"),
      table.subject_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const app_config = pgTable(
  "app_config",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    key: text().notNull(),
    value: text(),
    public: boolean().default(false).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    uniqueIndex("unique_app_config_key").using(
      "btree",
      table.key.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const shop_events = pgTable(
  "shop_events",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "shop_events_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    shop_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    user_id: bigint({ mode: "number" }),
    model_id: text(),
    model_type: text(),
    name: varchar({ length: 255 }).notNull(),
    payload: jsonb(),
    tx_id: text().default(`pg_current_xact_id()`),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("idx_shop_events_created_at").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("idx_shop_events_model_id").using(
      "btree",
      table.model_id.asc().nullsLast().op("text_ops"),
    ),
    index("idx_shop_events_model_type").using(
      "btree",
      table.model_type.asc().nullsLast().op("text_ops"),
    ),
    index("idx_shop_events_name").using(
      "btree",
      table.name.asc().nullsLast().op("text_ops"),
    ),
    index("idx_shop_events_shop_id").using(
      "btree",
      table.shop_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_shop_events_user_id").using(
      "btree",
      table.user_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const bundles = pgTable(
  "bundles",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "bundles_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    limit: integer().default(0),
    order: integer(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    product_id: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    min_limit: integer().default(0).notNull(),
    guid: uuid().defaultRandom().notNull(),
  },
  (table) => [
    index("bundles_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_bundles_product_id").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const checkouts = pgTable(
  "checkouts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    user_id: integer(),
    restaurant_id: integer().notNull(),
    payload: jsonb(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    payment_id: integer().notNull(),
  },
  (table) => [unique("checkouts_payment_id_unique").on(table.payment_id)],
);

export const closing_reports = pgTable("closing_reports", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }).notNull(),
  starting_balances: jsonb().default({}),
  closing_balances: jsonb().default({}),
  differences: jsonb().default({}),
  notes: text(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  date: timestamp({ mode: "string" }).defaultNow(),
  shift_id: uuid(),
});

export const coin_programs = pgTable(
  "coin_programs",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    shop_id: bigint({ mode: "number" }),
    shop_group_id: bigint({ mode: "number" }),
    name: text().notNull(),
    coin_type: text().notNull(),
    // Singular noun shown to customers — "stamp", "point".
    coin_label: text().notNull(),
    // Key into the icon map, never a path.
    icon: text(),
    earn_rules: jsonb().default([]).notNull(),
    max_coins_per_payment: integer(),
    validity_months: integer(),
    enabled: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    // P1-03 expand: canonical owner. Nullable during the compatibility window;
    // the shop_id/shop_group_id pair is retained until the contract migration.
    // FKs, owner-based uniques and the composite (id, business_unit_id) unique
    // are added in migration 0135 (see business_units defined later in this
    // file, which prevents an in-callback FK reference here).
    business_unit_id: bigint({ mode: "number" }),
    // Required program-specific wallet presentation. The FK is added in the
    // wallet design migrations because wallet_pass_designs is declared later in
    // this file. It is composite over (design, owner, kind), so a program can
    // only select a same-owner coin design.
    wallet_pass_design_id: uuid().notNull(),
    wallet_pass_kind: text()
      .generatedAlwaysAs(sql`'coin'::text`)
      .notNull(),
  },
  (table) => [
    index("coin_programs_shop_id_idx")
      .using("btree", table.shop_id.asc().nullsLast().op("int8_ops"))
      .where(sql`${table.shop_id} IS NOT NULL`),
    index("coin_programs_shop_group_id_idx")
      .using("btree", table.shop_group_id.asc().nullsLast().op("int8_ops"))
      .where(sql`${table.shop_group_id} IS NOT NULL`),
    uniqueIndex("coin_programs_shop_coin_type_unique")
      .on(table.shop_id, table.coin_type)
      .where(sql`${table.shop_id} IS NOT NULL`),
    uniqueIndex("coin_programs_group_coin_type_unique")
      .on(table.shop_group_id, table.coin_type)
      .where(sql`${table.shop_group_id} IS NOT NULL`),
    check(
      "coin_programs_exactly_one_owner_check",
      sql`num_nonnulls(${table.shop_id}, ${table.shop_group_id}) = 1`,
    ),
  ],
);

export const membership_programs = pgTable(
  "membership_programs",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    shop_id: bigint({ mode: "number" }),
    shop_group_id: bigint({ mode: "number" }),
    name: text().notNull(),
    description: text(),
    enabled: boolean().default(true).notNull(),
    // 'manual' = curated member list; 'auto' = the base program every customer
    // of an applicable shop is enrolled in. See
    // docs/plans/loyalty/membership_cards.md#base-memberships-auto-enrolment-and-applicability
    enrolment: text().default("manual").notNull(),
    validity_months: integer(),
    cap_period: text(),
    cap_count: integer(),
    // P1-04 monetary cap: a per-period budget the applied discount value
    // accumulates against (e.g. 500 CHF/month of 100%-off orders). Independent of
    // cap_count; either or both may be set, sharing cap_period/period_timezone.
    cap_amount: numeric({ precision: 18, scale: 4 }),
    // P1-04: IANA timezone the calendar period boundaries are computed in.
    // Required whenever a cap is configured (a shop program derives it from its
    // restaurant; a group admin selects it). Captured once with the cap so a
    // later restaurant/group timezone change never moves existing windows.
    period_timezone: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    // P1-03 expand: canonical owner (see coin_programs.business_unit_id).
    business_unit_id: bigint({ mode: "number" }),
    // Required program-specific wallet presentation. See the corresponding
    // field on coin_programs for why its FK lives in the migration.
    wallet_pass_design_id: uuid().notNull(),
    wallet_pass_kind: text()
      .generatedAlwaysAs(sql`'membership'::text`)
      .notNull(),
  },
  (table) => [
    check(
      "membership_programs_enrolment_check",
      sql`${table.enrolment} IN ('manual', 'auto')`,
    ),
    index("membership_programs_shop_id_idx")
      .on(table.shop_id)
      .where(sql`${table.shop_id} IS NOT NULL`),
    index("membership_programs_shop_group_id_idx")
      .on(table.shop_group_id)
      .where(sql`${table.shop_group_id} IS NOT NULL`),
    uniqueIndex("membership_programs_shop_name_unique")
      .on(table.shop_id, table.name)
      .where(sql`${table.shop_id} IS NOT NULL`),
    uniqueIndex("membership_programs_group_name_unique")
      .on(table.shop_group_id, table.name)
      .where(sql`${table.shop_group_id} IS NOT NULL`),
    check(
      "membership_programs_exactly_one_owner_check",
      sql`num_nonnulls(${table.shop_id}, ${table.shop_group_id}) = 1`,
    ),
    // A period is set iff at least one cap dimension (count or amount) is.
    check(
      "membership_programs_cap_pair_check",
      sql`(${table.cap_period} IS NULL) = (${table.cap_count} IS NULL AND ${table.cap_amount} IS NULL)`,
    ),
    check(
      "membership_programs_cap_period_check",
      sql`${table.cap_period} IS NULL OR ${table.cap_period} IN ('week', 'month', 'year')`,
    ),
    check(
      "membership_programs_cap_count_check",
      sql`${table.cap_count} IS NULL OR ${table.cap_count} > 0`,
    ),
    check(
      "membership_programs_cap_amount_check",
      sql`${table.cap_amount} IS NULL OR ${table.cap_amount} > 0`,
    ),
  ],
);

export const membership_members = pgTable(
  "membership_members",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    membership_program_id: uuid().notNull(),
    profile_id: uuid().notNull(),
    status: text().default("active").notNull(),
    joined_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
    added_by_identity_id: bigint({ mode: "number" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    unique("membership_members_program_profile_unique").on(
      table.membership_program_id,
      table.profile_id,
    ),
    index("membership_members_profile_id_idx").on(table.profile_id),
    index("membership_members_program_id_idx").on(table.membership_program_id),
    foreignKey({
      columns: [table.membership_program_id],
      foreignColumns: [membership_programs.id],
      name: "membership_members_membership_program_id_fkey",
    }),
    check(
      "membership_members_status_check",
      sql`${table.status} IN ('active', 'revoked', 'expired')`,
    ),
  ],
);

// P1-04: append-only ledger of finite (capped) membership benefit usage. A cap
// is program-wide, so a capture is one +1 per (payment, program); a refund/void
// appends a -1 referencing the capture, and voiding a credit appends the inverse.
// Signed amounts net to the consumption in a calendar period. Never edited or
// deleted. Cross-table FKs and the security-definer capture/reverse functions
// live in migration 0136 and sql/functions/membership_programs/usage_*.sql.
// See docs/plans/loyalty/membership_phase_1_completion.md.
export const membership_usage = pgTable(
  "membership_usage",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    membership_program_id: uuid().notNull(),
    profile_id: uuid().notNull(),
    business_unit_id: bigint({ mode: "number" }).notNull(),
    // The sale payment for a capture / sale-void; the credit payment for a
    // refund reversal or a credit-void reactivation.
    payment_id: bigint({ mode: "number" }).notNull(),
    promotion_id: uuid(),
    // The captured promotion application this row derives from (evidence).
    application_id: text(),
    // Local period start in the program's period_timezone, stored so a later
    // timezone change never moves a recorded window.
    period_start: date({ mode: "string" }).notNull(),
    // Signed +1/-1 use count (for cap_count).
    amount: integer().notNull(),
    // Signed discount value consumed/restored (for cap_amount); 0 for a program
    // capped only by count.
    value: numeric({ precision: 18, scale: 4 }).default("0").notNull(),
    event_kind: text().notNull(),
    // For reverse/reactivate: the usage row being compensated.
    compensation_of: uuid(),
    // The restaurant the checkout happened at (shop_events.shop_id), including
    // for a group-owned program.
    checkout_restaurant_id: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    check(
      "membership_usage_event_kind_check",
      sql`${table.event_kind} IN ('capture', 'reverse', 'reactivate')`,
    ),
    check("membership_usage_amount_check", sql`${table.amount} IN (1, -1)`),
    index("membership_usage_program_profile_period_idx").on(
      table.membership_program_id,
      table.profile_id,
      table.period_start,
    ),
    index("membership_usage_payment_id_idx").on(table.payment_id),
    // A cap is program-wide: at most one capture per (payment, program). This is
    // also the capture idempotency guard — a retry finds the existing row.
    uniqueIndex("membership_usage_capture_unique")
      .on(table.payment_id, table.membership_program_id)
      .where(sql`${table.event_kind} = 'capture'`),
    // A capture is reversed at most once, and a reverse is reactivated at most
    // once — the compensation target plus kind is unique.
    uniqueIndex("membership_usage_compensation_unique")
      .on(table.compensation_of, table.event_kind)
      .where(sql`${table.compensation_of} IS NOT NULL`),
  ],
);

// Which restaurants a program enrols customers from. Ownership (shop_id XOR
// shop_group_id on membership_programs) says who administers a program;
// applicability says where it applies. A shop-owned program applies implicitly
// to its one shop and needs no rows here; a group-owned program carries a row
// per restaurant it covers (seeded to all group shops, trimmable in the editor).
// See docs/plans/loyalty/membership_cards.md#ownership-is-not-applicability
export const membership_program_restaurants = pgTable(
  "membership_program_restaurants",
  {
    membership_program_id: uuid().notNull(),
    restaurant_id: integer().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    primaryKey({
      columns: [table.membership_program_id, table.restaurant_id],
      name: "membership_program_restaurants_pkey",
    }),
    index("membership_program_restaurants_restaurant_id_idx").on(
      table.restaurant_id,
    ),
    foreignKey({
      columns: [table.membership_program_id],
      foreignColumns: [membership_programs.id],
      name: "membership_program_restaurants_program_id_fkey",
    }).onDelete("cascade"),
  ],
);

// A pre-payment reservation of one finite loyalty resource (membership use,
// coin quantity, or gift-card value) for one payment, so a terminal charge can
// never succeed and then fail to record because another checkout consumed the
// last of it across the provider gap. Generic across resource kinds; Phase 1
// enables `membership` (and `coin` redemption) — the gift-card columns ship on
// the table but its handler is Phase 2. An active hold reduces availability; the
// permanent ledgers (membership_usage / coin_transactions / gift_card_transactions)
// remain the record of consumption. See
// docs/plans/loyalty/phase_1_membership_reservations.md
export const payment_resource_holds = pgTable(
  "payment_resource_holds",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    payment_id: bigint({ mode: "number" }).notNull(),
    resource_kind: text().notNull(),
    // Exactly one typed resource FK is set and must match resource_kind.
    membership_program_id: uuid(),
    coin_program_id: uuid(),
    gift_card_id: uuid(),
    // Required for membership/coin holds; a gift card may be anonymous.
    profile_id: uuid(),
    business_unit_id: bigint({ mode: "number" }),
    // The restaurant the checkout happened at (shop_events.shop_id).
    checkout_restaurant_id: integer(),
    // Quantity = membership uses or coins; value = monetary benefit/tender.
    reserved_quantity: integer().default(0).notNull(),
    reserved_value: numeric({ precision: 18, scale: 4 }).default("0").notNull(),
    currency: text(),
    // Membership period snapshot, so a later timezone change never moves it.
    period_start: date({ mode: "string" }),
    cap_period: text(),
    period_timezone: text(),
    // Only `active` reduces availability.
    state: text().default("active").notNull(),
    // Monotonic counter for optimistic concurrency (bumped on adjust).
    revision: integer().default(1).notNull(),
    // Content hash over the immutable snapshot (staleness guard). Nullable in
    // the slice; populated once the full prepare/adjust/claim contract lands.
    snapshot_hash: text(),
    // The permanent ledger row a consumed hold settled into (typed per kind).
    membership_usage_id: uuid(),
    coin_transaction_id: uuid(),
    gift_card_transaction_id: uuid(),
    release_reason: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    consumed_at: timestamp({ withTimezone: true, mode: "string" }),
    released_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    check(
      "payment_resource_holds_kind_check",
      sql`${table.resource_kind} IN ('membership', 'coin', 'gift_card')`,
    ),
    check(
      "payment_resource_holds_state_check",
      sql`${table.state} IN ('active', 'consumed', 'released')`,
    ),
    // Exactly one typed resource FK, matching resource_kind.
    check(
      "payment_resource_holds_one_resource_check",
      sql`(
        (${table.membership_program_id} IS NOT NULL)::int
        + (${table.coin_program_id} IS NOT NULL)::int
        + (${table.gift_card_id} IS NOT NULL)::int
      ) = 1
      AND (${table.resource_kind} <> 'membership' OR ${table.membership_program_id} IS NOT NULL)
      AND (${table.resource_kind} <> 'coin' OR ${table.coin_program_id} IS NOT NULL)
      AND (${table.resource_kind} <> 'gift_card' OR ${table.gift_card_id} IS NOT NULL)`,
    ),
    // A consumed hold references its settlement row; a released hold has a reason.
    check(
      "payment_resource_holds_terminal_check",
      sql`(${table.state} <> 'consumed' OR ${table.consumed_at} IS NOT NULL)
      AND (${table.state} <> 'released' OR ${table.released_at} IS NOT NULL)`,
    ),
    // One hold per (payment, resource) for the payment attempt's lifetime,
    // including released rows: a released pair cannot be recreated.
    uniqueIndex("payment_resource_holds_payment_membership_unique")
      .on(table.payment_id, table.membership_program_id)
      .where(sql`${table.membership_program_id} IS NOT NULL`),
    uniqueIndex("payment_resource_holds_payment_coin_unique")
      .on(table.payment_id, table.coin_program_id)
      .where(sql`${table.coin_program_id} IS NOT NULL`),
    uniqueIndex("payment_resource_holds_payment_gift_card_unique")
      .on(table.payment_id, table.gift_card_id)
      .where(sql`${table.gift_card_id} IS NOT NULL`),
    index("payment_resource_holds_payment_id_idx").on(table.payment_id),
    // Hot-path availability read for active membership holds in a period.
    index("payment_resource_holds_membership_active_idx")
      .on(table.membership_program_id, table.profile_id, table.period_start)
      .where(sql`${table.state} = 'active'`),
    index("payment_resource_holds_coin_active_idx")
      .on(table.coin_program_id, table.profile_id)
      .where(sql`${table.state} = 'active'`),
  ],
);

// Which persisted promotion applications caused a resource hold. Does not
// duplicate payment_promotion_applications; the parent hold stays authoritative
// for the total quantity/value (one membership use per payment/program even when
// several application rows aggregate into it). See the same plan doc.
export const payment_resource_hold_applications = pgTable(
  "payment_resource_hold_applications",
  {
    hold_id: uuid().notNull(),
    application_id: text().notNull(),
    resource_quantity: integer(),
    application_value: numeric({ precision: 18, scale: 4 }),
  },
  (table) => [
    primaryKey({
      columns: [table.hold_id, table.application_id],
      name: "payment_resource_hold_applications_pkey",
    }),
    foreignKey({
      columns: [table.hold_id],
      foreignColumns: [payment_resource_holds.id],
      name: "payment_resource_hold_applications_hold_id_fkey",
    }).onDelete("cascade"),
  ],
);

// A distributable wallet-pass artifact (Apple/Google), one row per (kind,
// subject) with its own opaque `serial`. The serial is both the pass
// serialNumber and the bearer token the download route resolves — it is put in
// the pass's QR only for gift cards; membership QRs carry `lemmon-loyalty:<profile>`
// so a scan identifies the person at the till. Separate from membership_members
// (the enrolment fact): this is the artifact, and it also covers gift cards.
// See docs/plans/loyalty/wallet_passes.md
export const wallet_passes = pgTable(
  "wallet_passes",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    serial: text().notNull(),
    kind: text().notNull(),
    membership_program_id: uuid(),
    gift_card_id: uuid(),
    coin_program_id: uuid(),
    // Required for membership/coin passes; null for an anonymous gift card.
    profile_id: uuid(),
    shop_id: bigint({ mode: "number" }),
    shop_group_id: bigint({ mode: "number" }),
    status: text().default("active").notNull(),
    // Random per-pass bearer used by Apple's update web service. It is created
    // when the pass registry row is minted and remains stable across renders.
    authentication_token: text(),
    // The design (id, revision) each provider last rendered, recorded
    // independently so one provider can retry without re-rendering the other.
    // A pass is stale for a provider when the pair differs from its program's
    // current selection.
    apple_design_id: uuid(),
    apple_design_revision: integer(),
    google_design_id: uuid(),
    google_design_revision: integer(),
    // Lets a future "get latest pass" endpoint 304 when nothing changed.
    content_hash: text(),
    // Content-addressed R2 object containing the current signed .pkpass.
    storage_key: text(),
    // Signed Save-to-Google-Wallet link for the pre-created object.
    google_save_url: text(),
    // When the pass was emailed to the member. Guards against re-emailing the
    // same pass across enrolment/attach paths. See wallet_passes.md.
    emailed_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    last_rendered_at: timestamp({ withTimezone: true, mode: "string" }),
    last_push_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("wallet_passes_serial_unique").on(table.serial),
    // One membership pass per (program, profile).
    uniqueIndex("wallet_passes_membership_unique")
      .on(table.membership_program_id, table.profile_id)
      .where(sql`${table.kind} = 'membership'`),
    // One coin pass per (coin program, profile) — a coin pass is its own pass.
    uniqueIndex("wallet_passes_coin_unique")
      .on(table.coin_program_id, table.profile_id)
      .where(sql`${table.kind} = 'coin'`),
    index("wallet_passes_profile_id_idx").on(table.profile_id),
    check(
      "wallet_passes_kind_check",
      sql`${table.kind} IN ('membership', 'gift_card', 'coin')`,
    ),
    // Exactly one subject, and a profile is present unless it's a gift card.
    check(
      "wallet_passes_subject_check",
      sql`num_nonnulls(${table.membership_program_id}, ${table.gift_card_id}, ${table.coin_program_id}) = 1`,
    ),
    foreignKey({
      columns: [table.membership_program_id],
      foreignColumns: [membership_programs.id],
      name: "wallet_passes_membership_program_id_fkey",
    }).onDelete("cascade"),
  ],
);

// Apple Wallet pass-update web service device registrations. A device that has
// installed a pass registers itself here (POST /v1/devices/.../:serialNumber)
// so the DBOS worker can push an empty APNs notification to its `push_token`
// when the pass changes; the device then re-downloads the fresh pass.
// See docs/plans/wallet_passes/05-live-updates.md and
// docs/architecture/wallet/apple/pass_updates.md
export const wallet_pass_registrations = pgTable(
  "wallet_pass_registrations",
  {
    // Apple's per-device opaque id (not our profile) — scopes a registration.
    device_library_id: text().notNull(),
    // The pass.* identifier, echoed back on every device callback.
    pass_type_id: text().notNull(),
    // wallet_passes.serial — the pass this device registered for.
    serial_number: text().notNull(),
    // APNs token the push is sent to; refreshed on re-registration.
    push_token: text().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    // One registration per (device, pass); a re-register updates push_token.
    primaryKey({
      columns: [table.device_library_id, table.serial_number],
      name: "wallet_pass_registrations_pkey",
    }),
    // "Which devices registered this pass?" — the push fan-out on change.
    index("wallet_pass_registrations_serial_idx").on(table.serial_number),
  ],
);

export const coin_transactions = pgTable(
  "coin_transactions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    // Null = earned but unclaimed. Most POS payments have no customer.
    customer_id: uuid(),
    // Balance identity shared across restaurant-specific customer records.
    profile_id: uuid().notNull(),
    // Denormalised beside coin_program_id: the wallet pass renderer keys its
    // image cache on it and reads it without a join.
    coin_type: text().notNull(),
    amount: integer().notNull(),
    // 'earn' | 'redeem' | 'expire'
    type: text().notNull(),
    source: text(),
    source_id: text(),
    note: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    coin_program_id: uuid(),
    // The payment that caused the row; also the claim key.
    payment_id: integer(),
    // The other party — referral, future gifting.
    related_customer_id: uuid(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    // Per-payment idempotency: coins_capture is called from two places.
    uniqueIndex("coin_transactions_payment_program_type_idx")
      .using(
        "btree",
        table.payment_id.asc().nullsLast().op("int4_ops"),
        table.coin_program_id.asc().nullsLast().op("uuid_ops"),
        table.type.asc().nullsLast().op("text_ops"),
      )
      .where(sql`payment_id IS NOT NULL`),
    // Balance reads, FIFO ordering.
    index("coin_transactions_balance_idx").using(
      "btree",
      table.customer_id.asc().nullsLast().op("uuid_ops"),
      table.coin_program_id.asc().nullsLast().op("uuid_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("coin_transactions_profile_balance_idx").using(
      "btree",
      table.profile_id.asc().nullsLast().op("uuid_ops"),
      table.coin_program_id.asc().nullsLast().op("uuid_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    // Claim lookup.
    index("coin_transactions_unclaimed_idx")
      .using("btree", table.payment_id.asc().nullsLast().op("int4_ops"))
      .where(sql`customer_id IS NULL`),
    // Expiry sweep.
    index("coin_transactions_expiry_idx")
      .using("btree", table.expires_at.asc().nullsLast().op("timestamptz_ops"))
      .where(sql`expires_at IS NOT NULL AND type = 'earn'`),
    // One expire row per earn row, ever: makes coins_expire_due idempotent and
    // settles a race between two concurrent sweeps. source_id holds the earn
    // row's id for expire rows.
    uniqueIndex("coin_transactions_expire_source_idx")
      .using("btree", table.source_id.asc().nullsLast().op("text_ops"))
      .where(sql`type = 'expire'`),
    foreignKey({
      columns: [table.coin_program_id],
      foreignColumns: [coin_programs.id],
      name: "coin_transactions_coin_program_id_fkey",
    }),
  ],
);

export const customer_addresses = pgTable("customer_addresses", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  customer_id: uuid().notNull(),
  first_name: text(),
  last_name: text(),
  company_name: text(),
  company_registration: text(),
  street: text().notNull(),
  postal_code: text().notNull(),
  city: text().notNull(),
  email: text().notNull(),
  phone: text().notNull(),
  notes: text(),
  enabled: boolean().default(true).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }),
  updated_at: timestamp({ withTimezone: true, mode: "string" }),
  name: text(),
  latitude: text(),
  longitude: text(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }),
});

export const applied_discounts = pgTable("applied_discounts", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "applied_discounts_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }),
  source: varchar({ length: 30 }).notNull(),
  type: varchar({ length: 30 }).notNull(),
  category: varchar({ length: 255 }),
  amount: doublePrecision().notNull(),
  target_sum: doublePrecision().notNull(),
  net: doublePrecision().notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  restaurant_id: integer(),
  guid: uuid().defaultRandom().notNull(),
  discount_type_id: uuid(),
});

export const durable_jobs = pgTable(
  "durable_jobs",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "durable_jobs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    deterministic_id: text().notNull(),
    type: text().notNull(),
    payload: jsonb().default({}).notNull(),
    status: text().default("pending").notNull(),
    version: integer().default(0).notNull(),
    attempts: integer().default(0).notNull(),
    locked_by: text(),
    locked_until: timestamp({ withTimezone: true, mode: "string" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    source_event_id: bigint({ mode: "number" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    finished_at: timestamp({ withTimezone: true, mode: "string" }),
    last_error: text(),
  },
  (table) => [
    uniqueIndex("durable_jobs_deterministic_id_idx").using(
      "btree",
      table.deterministic_id.asc().nullsLast().op("text_ops"),
    ),
    index("idx_durable_jobs_created_at").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("idx_durable_jobs_locked_until").using(
      "btree",
      table.locked_until.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("idx_durable_jobs_source_event_id").using(
      "btree",
      table.source_event_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_durable_jobs_status").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.source_event_id],
      foreignColumns: [shop_events.id],
      name: "durable_jobs_source_event_id_shop_events_id_fk",
    }),
  ],
);

export const customer_checkouts = pgTable("customer_checkouts", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  restaurant_id: integer().notNull(),
  status: text().default("new").notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  table_id: bigint({ mode: "number" }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }),
  customer_id: uuid(),
  group_id: text(),
  type: text().notNull(),
  payload: jsonb().notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  service_method: text().notNull(),
  payment_method: text().notNull(),
  contents: jsonb(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  payment_id: bigint({ mode: "number" }),
  order_type: text(),
  group_order_id: uuid(),
  subject_type: text().default("group_order").notNull(),
  reservation_request_id: uuid(),
  reservation_id: uuid(),
});

// Staff-side POS checkout lifecycle record. Mirrors customer_checkouts for the
// apps/web cashier flow: payments are staged against an open staff_checkout
// (payments.staff_checkout_id) and the order is materialized only at finalize,
// enabling split/partial payments on non-table (cart) orders plus crash
// recovery. Separate from the legacy per-payment `checkouts` audit table.
// See docs/plans/checkout_editor/.
export const staff_checkouts = pgTable(
  "staff_checkouts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: integer().notNull(),
    user_id: integer().notNull(), // staff member who opened the checkout
    service_method: text().notNull(), // 'takeaway' / 'dine-in' / 'delivery'
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    table_id: integer(), // null for plain cart checkouts
    status: text().default("open").notNull(), // open / completed / cancelled
    payment_mode: text(), // null / 'split' / 'partial' (locked on first staged payment)
    // Canonical loyalty identity. Intentionally no FK: checkout/payment history
    // must survive profile lifecycle cleanup.
    profile_id: uuid(),
    // Shop-local CRM identity resolved from (profile_id, restaurant_id).
    customer_id: uuid(),
    cart: jsonb().notNull(), // items in create_order format (serialized by the tRPC create mutation)
    totals: jsonb().default({}).notNull(),
    discount: jsonb().default({}).notNull(),
    // Order-creation metadata not derivable from the cart items alone
    // (deliveryFee, isMultiCourse, triggeredFoodTypeIds, reservationId,
    // personas). Captured at create and replayed into create_order at finalize
    // so a split/partial-paid order keeps the same shape as the regular path.
    metadata: jsonb().default({}).notNull(),
    tips: numeric(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_id: bigint({ mode: "number" }), // filled on finalization
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("staff_checkouts_open_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int4_ops"),
        table.status.asc().nullsLast().op("text_ops"),
      )
      .where(sql`(status = 'open'::text)`),
  ],
);

export const delivery_information = pgTable("delivery_information", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "delivery_information_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }).notNull(),
  first_name: varchar({ length: 255 }),
  last_name: varchar({ length: 255 }),
  company_name: varchar({ length: 255 }),
  company_registration: varchar({ length: 255 }),
  street: varchar({ length: 255 }),
  postal_code: varchar({ length: 255 }),
  city: varchar({ length: 255 }),
  email: varchar({ length: 255 }),
  phone: varchar({ length: 255 }),
  notes: varchar({ length: 255 }),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  guid: uuid().defaultRandom().notNull(),
  name: text(),
});

export const discount_types = pgTable("discount_types", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }).notNull(),
  title: text().notNull(),
  default: jsonb().notNull(),
  allow_changes: boolean().default(false).notNull(),
  applicable_only_on: jsonb(),
  deleted_at: timestamp({ withTimezone: true, mode: "string" }),
});

export const events = pgTable("events", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  restaurant_id: integer().notNull(),
  title: text().notNull(),
  subtitle: text(),
  date: date().notNull(),
  time: time().notNull(),
  image: text().notNull(),
  description: text().notNull(),
  long_description: text(),
  location: text(),
  duration: varchar({ length: 64 }),
  capacity: integer(),
  ticket_types: jsonb(),
  tags: jsonb().default([]).notNull(),
  need_to_know: jsonb(),
  schedule: jsonb(),
  price_from: numeric({ precision: 12, scale: 2 }),
  created_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  disabled: boolean(),
});

export const extra_bundle = pgTable(
  "extra_bundle",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "extra_bundle_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    entity_id: integer().notNull(),
    entity_type: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    bundle_id: bigint({ mode: "number" }).notNull(),
    price: numeric({ precision: 8, scale: 2 }),
    order: integer(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
  },
  (table) => [
    index("extra_bundle_bundle_id_idx").using(
      "btree",
      table.bundle_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const extra_images = pgTable("extra_images", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "extra_images_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  filename: varchar({ length: 255 }).notNull(),
  extra_id: integer().notNull(),
  type: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  guid: uuid().defaultRandom().notNull(),
});

export const extras = pgTable(
  "extras",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "extras_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    title: varchar({ length: 255 }).notNull(),
    description: varchar({ length: 255 }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    status: varchar({ length: 255 }).default("available").notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    color: text(),
    price: numeric({ precision: 8, scale: 2 }),
  },
  (table) => [
    index("extras_restaurant_id_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const failed_jobs = pgTable(
  "failed_jobs",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "failed_jobs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    uuid: varchar({ length: 255 }).notNull(),
    connection: text().notNull(),
    queue: text().notNull(),
    payload: text().notNull(),
    exception: text().notNull(),
    failed_at: timestamp({ mode: "string" })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    uniqueIndex("failed_jobs_uuid_idx").using(
      "btree",
      table.uuid.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const floor_plans = pgTable(
  "floor_plans",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text().notNull(),
    rows: integer().notNull(),
    columns: integer().notNull(),
    plan: json().default([]).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    rank: integer(),
  },
  (table) => [
    index("idx_floor_plans_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const group_orders = pgTable(
  "group_orders",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    table_id: bigint({ mode: "number" }),
    status: text().default("active").notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    type: text().default("multiple").notNull(),
    character_pack_name: text(),
  },
  (table) => [
    uniqueIndex("uniq_active_group_order_per_table")
      .using("btree", table.table_id.asc().nullsLast().op("int8_ops"))
      .where(sql`((status = 'active'::text) AND (type = 'multiple'::text))`),
  ],
);

export const group_orders_members = pgTable("group_orders_members", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  group_order_id: uuid().notNull(),
  customer_id: uuid().notNull(),
  payload: jsonb().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }),
  updated_at: timestamp({ withTimezone: true, mode: "string" }),
});

export const idempotency_keys = pgTable(
  "idempotency_keys",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    key: text().notNull(),
    value: jsonb(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    uniqueIndex("unique_idempotency_keys_key").using(
      "btree",
      table.key.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const jobs = pgTable(
  "jobs",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "jobs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    queue: varchar({ length: 255 }).notNull(),
    payload: text().notNull(),
    attempts: integer().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    reserved_at: bigint({ mode: "number" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    available_at: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    created_at: bigint({ mode: "number" }).notNull(),
  },
  (table) => [
    index("jobs_queue_idx").using(
      "btree",
      table.queue.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const materialized_order_totals = pgTable("materialized_order_totals", {
  order_id: integer().primaryKey().notNull(),
  restaurant_id: integer().notNull(),
  total_revenue: numeric().default("0").notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const materialized_orders = pgTable("materialized_orders", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "materialized_orders_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  restaurant_id: integer().notNull(),
  payload: jsonb().notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const materialized_payments = pgTable("materialized_payments", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "materialized_payments_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  restaurant_id: integer().notNull(),
  payload: jsonb().notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const menu_items = pgTable("menu_items", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "menu_items_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  title: varchar({ length: 255 }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  menu_id: bigint({ mode: "number" }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  product_category_id: bigint({ mode: "number" }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  menu_order: bigint({ mode: "number" }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const menus = pgTable("menus", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "menus_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  title: varchar({ length: 255 }),
  restaurant_id: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const migrations = pgTable("migrations", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "migrations_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  migration: varchar({ length: 255 }).notNull(),
  batch: integer().notNull(),
});

export const order_events = pgTable("order_events", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "order_events_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  event: varchar({ length: 255 }).notNull(),
  order_id: integer().notNull(),
  causer_id: integer(),
  status: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }).notNull(),
  guid: uuid().defaultRandom().notNull(),
});

export const order_food_statuses = pgTable("order_food_statuses", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "order_food_statuses_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  food_type_id: bigint({ mode: "number" }).notNull(),
  status: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  guid: uuid().defaultRandom().notNull(),
});

export const order_item_bundles = pgTable(
  "order_item_bundles",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "order_item_bundles_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_item_id: bigint({ mode: "number" }).notNull(),
    bundle_id: bigint({ mode: "number" }),
    bundle_name: varchar({ length: 255 }),
    option_id: bigint({ mode: "number" }),
    option_name: varchar({ length: 255 }),
    entity_id: integer().notNull(),
    entity_type: varchar({ length: 255 }).notNull(),
    entity_name: varchar({ length: 255 }),
    quantity_per_parent: numeric({ precision: 12, scale: 4 })
      .default("1")
      .notNull(),
    price: numeric({ precision: 8, scale: 2 }).notNull(),
    snapshot_status: varchar({ length: 32 }).default("unavailable").notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
  },
  (table) => [
    index("order_item_bundles_order_item_id_idx").using(
      "btree",
      table.order_item_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const order_partial_payment = pgTable("order_partial_payment", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "order_partial_payment_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  order_id: integer().notNull(),
  payment_id: integer().notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const order_print_statuses = pgTable(
  "order_print_statuses",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "order_print_statuses_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    printer_id: integer().notNull(),
    order_id: integer().notNull(),
    ticket_requested_at: timestamp({ mode: "string" }),
    ticket_requested_by: varchar({ length: 255 }),
    status: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    restaurant_id: integer(),
  },
  (table) => [
    index("order_print_statuses_created_at_idx").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamp_ops"),
    ),
    index("order_print_statuses_order_id_idx").using(
      "btree",
      table.order_id.asc().nullsLast().op("int4_ops"),
    ),
    index("order_print_statuses_printer_id_idx").using(
      "btree",
      table.printer_id.asc().nullsLast().op("int4_ops"),
    ),
    index("order_print_statuses_status_idx").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
    index("order_print_statuses_ticket_requested_at_idx").using(
      "btree",
      table.ticket_requested_at.asc().nullsLast().op("timestamp_ops"),
    ),
  ],
);

export const orders_customers_join = pgTable("orders_customers_join", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  customer_id: uuid().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const orders_payments_join = pgTable("orders_payments_join", {
  id: uuid().defaultRandom().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  payment_id: bigint({ mode: "number" }).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
});

export const parties = pgTable(
  "parties",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: uuid().notNull(),
    name: text(),
    notes: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
    table_id: integer(),
    customer_id: uuid(),
    reservation_id: uuid(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_pkey: bigint({ mode: "number" }),
  },
  (table) => [
    index("idx_parties_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("uuid_ops"),
    ),
  ],
);

export const password_reset_tokens = pgTable(
  "password_reset_tokens",
  {
    email: varchar({ length: 255 }).notNull(),
    token: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("password_resets_email_idx").using(
      "btree",
      table.email.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const payment_idempotency_keys = pgTable(
  "payment_idempotency_keys",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    payment_id: integer().notNull(),
    idempotency_key: text().notNull(),
    status: text().notNull(),
    expires_at: timestamp({ mode: "string" }),
    created_at: timestamp({ mode: "string" }).defaultNow(),
  },
  (table) => [
    uniqueIndex("unique_active_idempotency_keys")
      .using("btree", table.idempotency_key.asc().nullsLast().op("text_ops"))
      .where(
        sql`status IN ('initial'::text, 'sent'::text, 'success'::text, 'authorized'::text, 'confirmed'::text)`,
      ),
  ],
);

export const order_items = pgTable(
  "order_items",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "order_items_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    order_id: integer().notNull(),
    product_id: integer().notNull(),
    quantity: integer().notNull(),
    paid_quantity: integer(),
    price: doublePrecision().notNull(),
    notes: varchar({ length: 255 }),
    status: varchar({ length: 255 }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    tax_rate_id: uuid(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    food_type_id: bigint({ mode: "number" }),
    cart_id: text(),
    full_price: doublePrecision(),
  },
  (table) => [
    index("order_items_cart_id_idx").using(
      "btree",
      table.cart_id.asc().nullsLast().op("text_ops"),
    ),
    index("order_items_order_id_idx").using(
      "btree",
      table.order_id.asc().nullsLast().op("int4_ops"),
    ),
    index("order_items_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const orders = pgTable(
  "orders",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "orders_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    table_id: integer(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    user_id: bigint({ mode: "number" }),
    service_method: varchar({ length: 255 }),
    discount_takeaway: varchar({ length: 255 }),
    display_id: integer(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    parent_id: bigint({ mode: "number" }),
    is_grouped: smallint(),
    amount: doublePrecision().notNull(),
    tips: doublePrecision().default(0),
    partial_tips: doublePrecision(),
    delivery_fee: doublePrecision().default(0),
    notes: varchar({ length: 255 }),
    payment_method: varchar({ length: 255 }),
    session_id: varchar({ length: 255 }),
    device_id: varchar({ length: 255 }),
    status: varchar({ length: 255 }).notNull(),
    restaurant_status: varchar({ length: 255 }),
    bar_status: varchar({ length: 255 }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    party_guid: varchar({ length: 255 }),
    guid: uuid().defaultRandom().notNull(),
    shift_id: uuid(),
    is_multicourse: boolean().default(false).notNull(),
    customer_id: uuid(),
    created_timestamp: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_timestamp: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    group_order_id: uuid(),
    reservation_id: uuid(),
    reassigned_from_ids: jsonb(),
    personas: jsonb(),
    buyer_restaurant_id: integer(),
  },
  (table) => [
    index("idx_orders_active_orders").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("text_ops"),
      table.status.asc().nullsLast().op("int4_ops"),
      table.service_method.asc().nullsLast().op("text_ops"),
    ),
    index("idx_orders_created_at").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamp_ops"),
    ),
    index("idx_orders_created_timestamp").using(
      "btree",
      table.created_timestamp.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("idx_orders_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_orders_restaurant_id_created_at").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.created_at.desc().nullsFirst().op("int4_ops"),
    ),
    index("idx_orders_service_method").using(
      "btree",
      table.service_method.asc().nullsLast().op("text_ops"),
    ),
    index("idx_orders_shift_id").using(
      "btree",
      table.shift_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_orders_status").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
    index("idx_orders_table_id").using(
      "btree",
      table.table_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_orders_updated_timestamp").using(
      "btree",
      table.updated_timestamp.asc().nullsLast().op("timestamptz_ops"),
    ),
  ],
);

export const payment_integrations = pgTable("payment_integrations", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  type: text().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }).notNull(),
  credentials: jsonb().default({}).notNull(),
  settings: jsonb().default({}).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  user_ids: json().default([]).notNull(),
});

export const payment_terminal_transactions = pgTable(
  "payment_terminal_transactions",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "payment_terminal_transactions_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    payment_id: integer(),
    transaction_id: varchar({ length: 255 }),
    amount: varchar({ length: 255 }).notNull(),
    payload: text().notNull(),
    response: text(),
    status: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
  },
);

export const applied_discount_components = pgTable(
  "applied_discount_components",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "applied_discount_components_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    applied_discount_id: bigint({ mode: "number" }).notNull(),
    restaurant_id: integer(),
    source: varchar({ length: 30 }).notNull(),
    value_type: varchar({ length: 30 }).notNull(),
    input_value: doublePrecision().notNull(),
    discount_amount: doublePrecision().notNull(),
    category: varchar({ length: 255 }),
    discount_type_id: uuid(),
    metadata: jsonb().default({}).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_item_ids: bigint({ mode: "number" }).array().default([]).notNull(),
    cart_items_ids: text().array().default([""]).notNull(),
  },
  (table) => [
    index("applied_discount_components_discount_id_idx").using(
      "btree",
      table.applied_discount_id.asc().nullsLast().op("int8_ops"),
    ),
    foreignKey({
      columns: [table.applied_discount_id],
      foreignColumns: [applied_discounts.id],
      name: "applied_discount_components_applied_discount_id_fkey",
    }),
  ],
);

export const food_types = pgTable(
  "food_types",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "food_types_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    name: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
  },
  (table) => [
    index("idx_all_extras_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_food_types_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const customers = pgTable(
  "customers",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    name: text(),
    email: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    password: text(),
    phone: text(),
    tags: jsonb().default({}),
    restaurant_tags: text().array().default([]).notNull(),
    restaurant_id: integer(),
    status_level: text(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    birth_day_month: text(),
    birth_year: integer(),
    // The app account behind this record. `customers` is one row per person per
    // restaurant, so a profile owns many of these — the FK belongs on this side.
    profile_id: uuid(),
    // Per-restaurant referral link. Unused until referral_earn_rule.md.
    referred_by: uuid(),
  },
  (table) => [
    // Makes "find-or-create the customers row for this profile at this shop"
    // idempotent, so a retried claim cannot fork the identity.
    uniqueIndex("customers_profile_restaurant_idx")
      .using(
        "btree",
        table.profile_id.asc().nullsLast().op("uuid_ops"),
        table.restaurant_id.asc().nullsLast().op("int4_ops"),
      )
      .where(sql`profile_id IS NOT NULL`),
    // No FK to profiles: that table is declared further down this file, and the
    // extra-config callback runs during table construction, so referencing it
    // here would hit the temporal dead zone.
  ],
);

export const payment_terminals = pgTable(
  "payment_terminals",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "payment_terminals_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    name: varchar({ length: 255 }).notNull(),
    password: varchar({ length: 255 }).notNull(),
    client_id: varchar({ length: 255 }),
    client_secret: varchar({ length: 255 }),
    access_token: varchar({ length: 255 }),
    terminal_id: integer(),
    access_token_expiration: timestamp({ mode: "string" }),
    staff_id: json(),
    status: varchar({ length: 255 }).default("not_connected").notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    // Payrexx terminal-integration columns. These were applied to staging by the
    // parallel migration 0127_aromatic_mantis on feature/terminal-setup-payrexx-api
    // and are brought into this branch's canonical schema so the model reflects
    // reality; the reconciliation migration represents them idempotently. See
    // docs/plans/loyalty/phase_1_scope_and_migration_strategy.md.
    integration_id: uuid(),
    provider: text().default("nakka").notNull(),
    serial_number: text(),
    settings: jsonb().default({}).notNull(),
    location_id: text(),
  },
  (table) => [
    foreignKey({
      columns: [table.integration_id],
      foreignColumns: [payment_integrations.id],
      name: "payment_terminals_integration_id_fkey",
    }),
  ],
);

export const payment_transactions = pgTable(
  "payment_transactions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    status: text().notNull(),
    provider: text().notNull(),
    terminal_id: integer(),
    payload: jsonb().notNull(),
    logs: jsonb().notNull(),
    response: jsonb(),
    version: uuid().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    type: text().notNull(),
    // Back-reference to payments.id (the owning side is payments.payment_transaction_id).
    // Written by order_processing_create_payment right after the payment insert.
    payment_id: bigint({ mode: "number" }),
    providerId: text(),
    terminal_guid: uuid(),
    restaurant_id: integer(),
    result: jsonb(),
    integration_id: uuid(),
    integration_params: jsonb(),
    workflow_type: text(),
    workflow_id: text(),
  },
  (table) => [
    index("idx_payment_transations_workflow_id").using(
      "btree",
      table.workflow_id.asc().nullsLast().op("text_ops"),
    ),
    // Resolves a provider transaction reference (e.g. Payrexx transfer uuid) to a
    // Lemmon payment at payout-settlement time.
    index("idx_payment_transactions_provider_providerid").on(
      table.provider,
      table.providerId,
    ),
  ],
);

export const payments_partial = pgTable("payments_partial", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "payments_partial_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  user_id: integer().notNull(),
  // Nullable: staff_checkouts split/partial payments are for non-table (cart)
  // orders, so a partial payment can exist without a table. Table partials
  // still set it. See docs/plans/checkout_editor/.
  table_id: integer(),
  restaurant_id: integer().notNull(),
  orders: varchar({ length: 255 }).notNull(),
  tips: varchar({ length: 255 }),
  discount: varchar({ length: 255 }),
  discount_type: varchar({ length: 255 }),
  amount: varchar({ length: 255 }).notNull(),
  cart: text().notNull(),
  selection: text().notNull(),
  method: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  payment_id: integer(),
  items: jsonb(),
  guid: uuid().defaultRandom().notNull(),
});

export const payments_split = pgTable("payments_split", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  amount: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  sequence_number: text(),
  split_type: text(),
});

export const payrexx_transactions = pgTable(
  "payrexx_transactions",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "payrexx_transactions_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    payment_id: integer().notNull(),
    token: varchar({ length: 255 }),
    reference_id: integer().notNull(),
    payload: json(),
    response: json(),
    status: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
  },
  (table) => [
    index("payrexx_transactions_status_idx").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const permissions = pgTable(
  "permissions",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "permissions_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    guard_name: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    uniqueIndex("permissions_name_guard_name_idx").using(
      "btree",
      table.name.asc().nullsLast().op("text_ops"),
      table.guard_name.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const personal_access_tokens = pgTable(
  "personal_access_tokens",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "personal_access_tokens_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    tokenable_type: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    tokenable_id: bigint({ mode: "number" }).notNull(),
    name: varchar({ length: 255 }).notNull(),
    token: varchar({ length: 64 }).notNull(),
    abilities: text(),
    last_used_at: timestamp({ mode: "string" }),
    expires_at: timestamp({ mode: "string" }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    uniqueIndex("personal_access_tokens_token_idx").using(
      "btree",
      table.token.asc().nullsLast().op("text_ops"),
    ),
    index("personal_access_tokens_tokenable_type_tokenable_id_idx").using(
      "btree",
      table.tokenable_type.asc().nullsLast().op("int8_ops"),
      table.tokenable_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const pickup_details = pgTable("pickup_details", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "pickup_details_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  order_id: bigint({ mode: "number" }).notNull(),
  first_name: varchar({ length: 255 }),
  last_name: varchar({ length: 255 }),
  company_name: varchar({ length: 255 }),
  company_registration: varchar({ length: 255 }),
  email: varchar({ length: 255 }),
  phone: varchar({ length: 255 }),
  day: date(),
  time: time(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  guid: uuid().defaultRandom().notNull(),
  customer_id: uuid(),
  name: text(),
});

export const print_templates = pgTable("print_templates", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "print_templates_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  shop_id: bigint({ mode: "number" }).notNull(),
  type: text().default("kitchen_ticket").notNull(),
  name: text().notNull(),
  template: text().notNull(),
});

export const printer_product_category = pgTable(
  "printer_product_category",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "printer_product_category_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    printer_id: integer().notNull(),
    product_category_id: integer().notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("idx_printer_product_category_printer_id").using(
      "btree",
      table.printer_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_printer_product_category_product_category_id").using(
      "btree",
      table.product_category_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const printer_to_staff = pgTable(
  "printer_to_staff",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "printer_to_staff_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    printer_id: integer().notNull(),
    user_id: integer().notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("idx_printer_to_staff_printer_id").using(
      "btree",
      table.printer_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const payments = pgTable(
  "payments",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "payments_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    uuid: varchar({ length: 255 }).notNull(),
    transaction_code: varchar({ length: 256 }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_id: bigint({ mode: "number" }),
    table_id: integer(),
    terminal_id: integer(),
    table_orders: json(),
    partial_payment_id: integer(),
    amount: doublePrecision().notNull(),
    vat_percentage: doublePrecision(),
    vat_amount: doublePrecision(),
    discount_id: integer(),
    tips: doublePrecision(),
    provider: varchar({ length: 255 }).notNull(),
    method: varchar({ length: 255 }).default("card").notNull(),
    status: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    root_table_orders_ids: jsonb(),
    restaurant_id: integer(),
    user_id: integer(),
    guid: uuid().defaultRandom().notNull(),
    payment_transaction_id: uuid(),
    split_payment_id: uuid(),
    customer_id: uuid(),
    // Canonical loyalty identity. Kept separate from the historically
    // unconstrained customer_id column; intentionally has no FK.
    profile_id: uuid(),
    idempotency_keys: jsonb().default({}).notNull(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
    reference: text(),
    payment_type: text(),
    subtotal: doublePrecision(),
    type: text().default("sale").notNull(),
    reference_payment_id: integer(),
    shift_id: uuid(),
    customer_checkout_id: uuid(),
    staff_checkout_id: uuid(),
    created_timestamp: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_timestamp: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    rewards: jsonb().default({}),
    delivery_fee: doublePrecision(),
  },
  (table) => [
    index("idx_payments_authorized_created_at")
      .using("btree", table.created_at.asc().nullsLast().op("timestamp_ops"))
      .where(sql`((status)::text = 'authorized'::text)`),
    index("idx_payments_created_at").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamp_ops"),
    ),
    index("idx_payments_created_timestamp").using(
      "btree",
      table.created_timestamp.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("idx_payments_restaurant_id_created_at").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.created_at.desc().nullsFirst().op("timestamp_ops"),
    ),
    index("idx_payments_shift_id").using(
      "btree",
      table.shift_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_payments_updated_timestamp").using(
      "btree",
      table.updated_timestamp.asc().nullsLast().op("timestamptz_ops"),
    ),
    uniqueIndex("payments_guid_idx").using(
      "btree",
      table.guid.asc().nullsLast().op("uuid_ops"),
    ),
    index("payments_order_id_idx").using(
      "btree",
      table.order_id.asc().nullsLast().op("int8_ops"),
    ),
    index("payments_status_idx").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
    index("payments_staff_checkout_id_idx").using(
      "btree",
      table.staff_checkout_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("payments_profile_id_idx").using(
      "btree",
      table.profile_id.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("payments_transaction_code_idx").using(
      "btree",
      table.transaction_code.asc().nullsLast().op("text_ops"),
    ),
    uniqueIndex("payments_uuid_idx").using(
      "btree",
      table.uuid.asc().nullsLast().op("text_ops"),
    ),
  ],
);

/**
 * Queryable promotion applications captured at the payment boundary.
 *
 * `payments.rewards` remains the compatibility snapshot. These rows preserve
 * each individual application (including repeated uses of one promotion) so
 * accounting does not have to reinterpret that JSON on every read.
 */
export const payment_promotion_applications = pgTable(
  "payment_promotion_applications",
  {
    application_id: text().primaryKey().notNull(),
    payment_id: bigint({ mode: "number" }).notNull(),
    source_ordinal: integer().notNull(),
    promotion_id: text().notNull(),
    promotion_name: text(),
    reward_type: text(),
    value: numeric({ precision: 18, scale: 4 }).notNull(),
    calculation_version: integer(),
    tax_treatment: text(),
    reconstruction_status: text().default("captured").notNull(),
    reconstruction_evidence: jsonb().default({}).notNull(),
    // Server-validated structured reward/product/category display evidence.
    display_snapshot: jsonb(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("payment_promotion_applications_payment_id_idx").on(table.payment_id),
    index("payment_promotion_applications_promotion_id_idx").on(
      table.promotion_id,
    ),
    uniqueIndex("payment_promotion_applications_payment_ordinal_unique").on(
      table.payment_id,
      table.source_ordinal,
    ),
    foreignKey({
      columns: [table.payment_id],
      foreignColumns: [payments.id],
      name: "payment_promotion_applications_payment_id_fkey",
    }).onDelete("cascade"),
    check(
      "payment_promotion_applications_source_ordinal_check",
      sql`${table.source_ordinal} > 0`,
    ),
    check(
      "payment_promotion_applications_value_check",
      sql`${table.value} >= 0`,
    ),
    check(
      "payment_promotion_applications_reward_type_check",
      sql`${table.reward_type} IS NULL OR ${table.reward_type} IN ('product', 'product_category', 'product_discount', 'order_discount', 'special_price')`,
    ),
    check(
      "payment_promotion_applications_tax_treatment_check",
      sql`${table.tax_treatment} IS NULL OR ${table.tax_treatment} IN ('reduces_taxable_base', 'externally_funded')`,
    ),
    check(
      "payment_promotion_applications_reconstruction_status_check",
      sql`${table.reconstruction_status} IN ('captured', 'exact', 'reconstructed', 'estimated', 'unrecoverable')`,
    ),
  ],
);

export const payment_promotion_application_items = pgTable(
  "payment_promotion_application_items",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity({
      name: "payment_promotion_application_items_id_seq",
    }),
    application_id: text().notNull(),
    source_ordinal: integer().notNull(),
    order_item_id: bigint({ mode: "number" }),
    cart_item_id: text(),
    quantity: numeric({ precision: 18, scale: 4 }).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("payment_promotion_application_items_application_id_idx").on(
      table.application_id,
    ),
    index("payment_promotion_application_items_order_item_id_idx").on(
      table.order_item_id,
    ),
    index("payment_promotion_application_items_cart_item_id_idx").on(
      table.cart_item_id,
    ),
    uniqueIndex(
      "payment_promotion_application_items_application_ordinal_unique",
    ).on(table.application_id, table.source_ordinal),
    foreignKey({
      columns: [table.application_id],
      foreignColumns: [payment_promotion_applications.application_id],
      name: "payment_promotion_application_items_application_id_fkey",
    }).onDelete("cascade"),
    check(
      "payment_promotion_application_items_source_ordinal_check",
      sql`${table.source_ordinal} > 0`,
    ),
    check(
      "payment_promotion_application_items_identity_check",
      sql`${table.order_item_id} IS NOT NULL OR ${table.cart_item_id} IS NOT NULL`,
    ),
    check(
      "payment_promotion_application_items_quantity_check",
      sql`${table.quantity} > 0`,
    ),
  ],
);

export const payment_order_item_lines = pgTable(
  "payment_order_item_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity({
      name: "payment_order_item_lines_id_seq",
    }),
    restaurant_id: integer().notNull(),
    payment_id: bigint({ mode: "number" }).notNull(),
    order_id: bigint({ mode: "number" }).notNull(),
    order_item_id: bigint({ mode: "number" }).notNull(),
    quantity: numeric({ precision: 18, scale: 4 }).notNull(),
    unit_price: numeric({ precision: 18, scale: 4 }).notNull(),
    amount: numeric({ precision: 18, scale: 4 }).notNull(),
    line_type: text().default("item").notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("idx_payment_order_item_lines_order_item_id").using(
      "btree",
      table.order_item_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_payment_order_item_lines_payment_id").using(
      "btree",
      table.payment_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_payment_order_item_lines_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    uniqueIndex("payment_order_item_lines_payment_item_unique").using(
      "btree",
      table.payment_id.asc().nullsLast().op("int8_ops"),
      table.order_item_id.asc().nullsLast().op("int8_ops"),
    ),
    unique("payment_order_item_lines_payment_item_key").on(
      table.payment_id,
      table.order_item_id,
    ),
    index("payment_order_item_lines_order_item_idx").on(table.order_item_id),
    foreignKey({
      columns: [table.payment_id],
      foreignColumns: [payments.id],
      name: "payment_order_item_lines_payment_id_fkey",
    }),
    foreignKey({
      columns: [table.order_id],
      foreignColumns: [orders.id],
      name: "payment_order_item_lines_order_id_fkey",
    }),
    foreignKey({
      columns: [table.order_item_id],
      foreignColumns: [order_items.id],
      name: "payment_order_item_lines_order_item_id_fkey",
    }),
  ],
);

export const printers = pgTable(
  "printers",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "printers_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    name: varchar({ length: 255 }).notNull(),
    serial_number: varchar({ length: 255 }).notNull(),
    port: varchar({ length: 255 }).default("8043").notNull(),
    ip: varchar({ length: 255 }),
    device_id: varchar({ length: 255 }).default("local_printer").notNull(),
    print_receipt: smallint().default(0).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    user_ids: uuid().array(),
    category_ids: uuid().array(),
  },
  (table) => [
    index("idx_printers_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const product_categories = pgTable(
  "product_categories",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "product_categories_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    restaurant_id: integer().notNull(),
    status: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    product_ids: uuid().array(),
    color: text(),
  },
  (table) => [
    index("idx_product_categories_active").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.status.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_product_categories_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const product_extras = pgTable(
  "product_extras",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "product_extras_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    product_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    extra_id: bigint({ mode: "number" }).notNull(),
    price: varchar({ length: 255 }),
    order: integer(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("product_extras_extra_id_idx").using(
      "btree",
      table.extra_id.asc().nullsLast().op("int8_ops"),
    ),
    index("product_extras_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const product_images = pgTable(
  "product_images",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "product_images_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    filename: varchar({ length: 255 }).notNull(),
    product_id: integer().notNull(),
    type: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    processed_filename: varchar({ length: 255 }),
    thumbnail_filename: varchar({ length: 255 }),
    meta: jsonb(),
  },
  (table) => [
    index("idx_product_images_product_id").using(
      "btree",
      table.product_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const product_services = pgTable(
  "product_services",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "product_services_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    product_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    service_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("product_services_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
    index("product_services_service_id_idx").using(
      "btree",
      table.service_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const product_to_category = pgTable(
  "product_to_category",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "product_to_category_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    product_id: integer().notNull(),
    category_id: integer().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("idx_product_to_category_category_id").using(
      "btree",
      table.category_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_product_to_category_product_id").using(
      "btree",
      table.product_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const service_to_category = pgTable(
  "service_to_category",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "service_to_category_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    service_id: integer().notNull(),
    category_id: integer().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull().default(0),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("idx_service_to_category_category_id").using(
      "btree",
      table.category_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_service_to_category_service_id").using(
      "btree",
      table.service_id.asc().nullsLast().op("int4_ops"),
    ),
    unique("service_to_category_service_id_category_id_unique").on(
      table.service_id,
      table.category_id,
    ),
  ],
);

export const products_extra_products = pgTable(
  "products_extra_products",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "products_extra_products_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    product_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    extra_product_id: bigint({ mode: "number" }).notNull(),
    price: varchar({ length: 255 }).notNull(),
    order: integer().notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("products_extra_products_extra_product_id_idx").using(
      "btree",
      table.extra_product_id.asc().nullsLast().op("int8_ops"),
    ),
    index("products_extra_products_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const profile_addresses = pgTable("profile_addresses", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  profile_id: uuid().notNull(),
  first_name: text(),
  last_name: text(),
  company_name: text(),
  company_registration: text(),
  street: text().notNull(),
  postal_code: text().notNull(),
  city: text().notNull(),
  email: text().notNull(),
  phone: text().notNull(),
  notes: text(),
  enabled: boolean().default(true).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }),
  updated_at: timestamp({ withTimezone: true, mode: "string" }),
  name: text(),
  latitude: text(),
  longitude: text(),
});

export const profile_login_codes = pgTable("profile_login_codes", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  profile_id: uuid().notNull(),
  code: text().notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }),
  updated_at: timestamp({ withTimezone: true, mode: "string" }),
});

export const profiles = pgTable("profiles", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  name: text(),
  email: text(),
  // Birthday collected at sign-up (customer app), split so the year can be
  // skipped — mirrors the `customers` table. `birth_day_month` is 'MM-DD';
  // `birth_year` is optional. Both nullable; login-only flows never set them.
  birth_day_month: text(),
  birth_year: integer(),
  // Profile picture from the social provider at sign-up (Google only; Apple
  // shares none). Remote URL, not re-hosted.
  avatar_url: text(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  password: text(),
  phone: text(),
  tags: jsonb().default({}),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const products = pgTable(
  "products",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "products_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    weight: varchar({ length: 255 }),
    additional_info: varchar({ length: 50 }),
    price: varchar({ length: 255 }).notNull(),
    special_price: varchar({ length: 255 }),
    type: varchar({ length: 255 }).notNull(),
    is_custom: smallint().default(0).notNull(),
    description: text(),
    slug: varchar({ length: 255 }).notNull(),
    status: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    food_type_id: bigint({ mode: "number" }),
    restaurant_id: integer().notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    extra_limit: integer(),
    product_limit: integer(),
    guid: uuid().defaultRandom().notNull(),
    variants: jsonb(),
    tax_rate_id: uuid(),
    color: text(),
    item_type: text(),
    is_gift_card: boolean().default(false).notNull(),
    // 'gift_card' | 'table_credit'; null with is_gift_card means 'gift_card'.
    gift_card_kind: text(),
    // Hidden products the system sells on its own, e.g. 'table_credit_leftover'.
    system_kind: text(),
  },
  (table) => [
    uniqueIndex("products_restaurant_system_kind_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int4_ops"),
        table.system_kind.asc().nullsLast().op("text_ops"),
      )
      .where(sql`(system_kind IS NOT NULL)`),
    index("idx_products_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    index("idx_products_restaurant_status").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.status.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const receipt_requests = pgTable("receipt_requests", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "receipt_requests_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  restaurant_id: integer().notNull(),
  table_id: integer().notNull(),
  email: varchar({ length: 255 }),
  type: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" })
    .default(sql`CURRENT_TIMESTAMP`)
    .notNull(),
  updated_at: timestamp({ mode: "string" })
    .default(sql`CURRENT_TIMESTAMP`)
    .notNull(),
});

export const related_products = pgTable("related_products", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "related_products_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  product_id: integer().notNull(),
  related_product_id: integer().notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const removable_products = pgTable(
  "removable_products",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "removable_products_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    product_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    extra_id: bigint({ mode: "number" }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    order: integer(),
  },
  (table) => [
    index("removable_products_extra_id_idx").using(
      "btree",
      table.extra_id.asc().nullsLast().op("int8_ops"),
    ),
    index("removable_products_product_id_idx").using(
      "btree",
      table.product_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const promotions = pgTable(
  "promotions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    shop_id: bigint({ mode: "number" }),
    shop_group_id: bigint({ mode: "number" }),
    name: text().notNull(),
    enabled: boolean().default(true).notNull(),
    reward: jsonb().notNull(),
    requirements: jsonb().notNull(),
    days: jsonb().notNull(),
    created_at: timestamp({ mode: "string" }).defaultNow(),
    updated_at: timestamp({ mode: "string" }).defaultNow(),
    // Set on redemption tiers, null on ordinary promotions. One program can own
    // several tiers, so the FK points this way.
    coin_program_id: uuid(),
    membership_program_id: uuid(),
    // P1-03 expand: canonical owner (see coin_programs.business_unit_id). For a
    // program-owned promotion this equals the program's business_unit_id,
    // enforced in migration 0135 by composite FKs and the owner trigger.
    business_unit_id: bigint({ mode: "number" }),
  },
  (table) => [
    index("promotions_shop_id_idx")
      .on(table.shop_id)
      .where(sql`${table.shop_id} IS NOT NULL`),
    index("promotions_shop_group_id_idx")
      .on(table.shop_group_id)
      .where(sql`${table.shop_group_id} IS NOT NULL`),
    index("promotions_coin_program_id_idx")
      .using("btree", table.coin_program_id.asc().nullsLast().op("uuid_ops"))
      .where(sql`coin_program_id IS NOT NULL`),
    foreignKey({
      columns: [table.coin_program_id],
      foreignColumns: [coin_programs.id],
      name: "promotions_coin_program_id_fkey",
    }),
    index("promotions_membership_program_id_idx")
      .on(table.membership_program_id)
      .where(sql`${table.membership_program_id} IS NOT NULL`),
    foreignKey({
      columns: [table.membership_program_id],
      foreignColumns: [membership_programs.id],
      name: "promotions_membership_program_id_fkey",
    }),
    check(
      "promotions_exactly_one_owner_check",
      sql`num_nonnulls(${table.shop_id}, ${table.shop_group_id}) = 1`,
    ),
  ],
);

export const reservation_comments = pgTable("reservation_comments", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  reservation_id: uuid().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  user_id: bigint({ mode: "number" }).notNull(),
  message: text().notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const reservation_requests = pgTable("reservation_requests", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  restaurant_id: integer().notNull(),
  customer_id: uuid().notNull(),
  requested_time: timestamp({ withTimezone: true, mode: "string" }).notNull(),
  duration_mins: integer().default(60).notNull(),
  status: text().notNull(),
  party_size: integer().notNull(),
  notes: text(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  reservation_id: uuid(),
  party_name: text().notNull(),
  party_email: text(),
  party_phone: text(),
  deposit_amount: numeric({ precision: 10, scale: 2 }),
  deposit_gateway_id: text(),
  deposit_status: text().default("none"),
  requested_tag_ids: text(),
  request_kind: text().default("new").notNull(),
  target_reservation_id: uuid(),
  response_reason: text(),
  response_note: text(),
});

export const reservation_tables = pgTable(
  "reservation_tables",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    reservation_id: uuid().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    table_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    user_id: bigint({ mode: "number" }),
    status: text().notNull(),
  },
  (table) => [
    index("idx_reservation_tables_table_id").using(
      "btree",
      table.table_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const reservation_tag_assignments = pgTable(
  "reservation_tag_assignments",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    reservation_id: uuid().notNull(),
    tag_id: uuid().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    uniqueIndex("uq_reservation_tag").using(
      "btree",
      table.reservation_id.asc().nullsLast().op("uuid_ops"),
      table.tag_id.asc().nullsLast().op("uuid_ops"),
    ),
  ],
);

export const reservation_tags = pgTable("reservation_tags", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  restaurant_id: integer().notNull(),
  name: text().notNull(),
  color: text(),
  icon: text(),
  description: text(),
  sort_order: integer().default(0).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const reservations = pgTable(
  "reservations",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: integer().notNull(),
    request_id: uuid(),
    customer_id: uuid(),
    time: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    duration_mins: integer().default(60).notNull(),
    status: text().notNull(),
    party_size: integer().notNull(),
    notes: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    party_name: text().notNull(),
    party_email: text(),
    party_phone: text(),
    source: text(),
    deposit_amount: numeric({ precision: 10, scale: 2 }),
    deposit_gateway_id: text(),
    deposit_status: text().default("none"),
    has_changed: boolean(),
    hide_duration_to_guests: boolean(),
  },
  (table) => [
    index("idx_reservations_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const restaurant_assets = pgTable("restaurant_assets", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  // P1-05: nullable so a group-owned asset (business_unit_id is a group unit)
  // needs no restaurant; shop assets still set it. Dropped by the contract
  // migration. See docs/plans/loyalty/business_unit_assets.md.
  restaurant_id: integer(),
  type: text().notNull(),
  directory: text().notNull(),
  meta: jsonb().default({}).notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
  // P1-05: canonical owner. Nullable during the compatibility window; restaurant_id
  // is retained until the contract migration. The FK + index and backfill live in
  // migration 0137 (business_units is declared later in this file, which would make
  // an in-callback FK reference a TDZ error). Ownership is never taken from client
  // input. See docs/plans/loyalty/business_unit_assets.md.
  business_unit_id: bigint({ mode: "number" }),
});

export const restaurant_customers = pgTable("restaurant_customers", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  customer_id: uuid().notNull(),
  restaurant_id: integer().notNull(),
  status_level: text(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  tags: text().array().default([""]).notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const restaurant_jobs = pgTable("restaurant_jobs", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "restaurant_jobs_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  user_id: bigint({ mode: "number" }).notNull(),
  type: varchar({ length: 255 }).notNull(),
  status: varchar({ length: 255 }).notNull(),
  content: json(),
  result: varchar({ length: 255 }),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
  deleted_at: timestamp({ mode: "string" }),
});

export const restaurant_settings = pgTable("restaurant_settings", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "restaurant_settings_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  model_id: bigint({ mode: "number" }).notNull(),
  model_type: varchar({ length: 255 }).notNull(),
  key: varchar({ length: 255 }).notNull(),
  value: text(),
  value_type: varchar({ length: 255 }),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const restaurant_table_to_user = pgTable("restaurant_table_to_user", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "restaurant_table_to_user_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  table_id: integer().notNull(),
  user_id: integer().notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const restaurant_tables = pgTable(
  "restaurant_tables",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "restaurant_tables_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    hash: varchar({ length: 255 }).notNull(),
    type: varchar({ length: 255 }).notNull(),
    room: varchar({ length: 500 }),
    optional: varchar({ length: 500 }),
    status: varchar({ length: 255 }).notNull(),
    is_busy: integer().default(0).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    public_id: uuid().defaultRandom().notNull(),
    current_party_id: uuid(),
  },
  (table) => [
    index("idx_restaurant_tables_current_party_id").using(
      "btree",
      table.current_party_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("idx_restaurant_tables_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_restaurant_tables_status").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
    uniqueIndex("restaurant_tables_hash_restaurant_id_idx").using(
      "btree",
      table.hash.asc().nullsLast().op("int8_ops"),
      table.restaurant_id.asc().nullsLast().op("text_ops"),
    ),
    uniqueIndex("restaurant_tables_name_restaurant_id_idx").using(
      "btree",
      table.name.asc().nullsLast().op("int8_ops"),
      table.restaurant_id.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const shop_groups = pgTable(
  "shop_groups",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "shop_groups_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    name: text().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("shop_groups_guid_unique_idx").using(
      "btree",
      table.guid.asc().nullsLast().op("uuid_ops"),
    ),
  ],
);

// The canonical owner for Phase 1 loyalty resources (memberships, assets).
// Exactly one of restaurant_id / shop_group_id is set: a restaurant unit owns a
// single shop's resources; a group unit owns a shop-group's. A restaurant and
// its singleton group each get their own distinct unit — moving a restaurant
// between groups never changes its private restaurant unit. Never represent a
// group with a virtual restaurants row. See docs/plans/loyalty/business_units.md
// and docs/plans/loyalty/business_units_foundation.md.
export const business_units = pgTable(
  "business_units",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "business_units_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: bigint({ mode: "number" }),
    shop_group_id: bigint({ mode: "number" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    foreignKey({
      columns: [table.restaurant_id],
      foreignColumns: [restaurants.id],
      name: "business_units_restaurant_id_fkey",
    }).onDelete("restrict"),
    foreignKey({
      columns: [table.shop_group_id],
      foreignColumns: [shop_groups.id],
      name: "business_units_shop_group_id_fkey",
    }).onDelete("restrict"),
    // A restaurant/group has at most one unit. UNIQUE allows many NULLs, so
    // restaurant units (shop_group_id NULL) and group units (restaurant_id NULL)
    // coexist freely; the check below forbids a free-standing or dual-owner unit.
    unique("business_units_restaurant_id_unique").on(table.restaurant_id),
    unique("business_units_shop_group_id_unique").on(table.shop_group_id),
    check(
      "business_units_owner_exactly_one",
      sql`num_nonnulls(${table.restaurant_id}, ${table.shop_group_id}) = 1`,
    ),
  ],
);

export const restaurants = pgTable(
  "restaurants",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "restaurants_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    email: varchar({ length: 255 }).notNull(),
    public_email: text(),
    contact_person: varchar({ length: 255 }).notNull(),
    phone: varchar({ length: 255 }),
    address: varchar({ length: 255 }).notNull(),
    vat: varchar({ length: 255 }),
    bank_account: varchar({ length: 255 }),
    payment_fee: varchar({ length: 255 }),
    slug: varchar({ length: 255 }),
    payrexx_token: varchar({ length: 255 }),
    company_registration: varchar({ length: 255 }),
    payrexx_name: varchar({ length: 255 }),
    welcome_screen_image: varchar({ length: 255 }),
    receipt_logo: varchar({ length: 255 }),
    receipt_phone: varchar({ length: 255 }),
    receipt_message: varchar({ length: 255 }),
    food_receipt: smallint().default(0),
    bar_receipt: smallint().default(0),
    status: varchar({ length: 255 }).default("active").notNull(),
    onesignal_device_ids: text(),
    onboarded_by: integer(),
    onboarded_at: timestamp({ mode: "string" }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    timezone: text().default("Europe/Zurich"),
    locale: text().default("fr_CH"),
    currency: text().default("CHF"),
    jurisdiction: text().default("CH"),
    geolocation: jsonb().default({}),
    feature_flags: jsonb().default({}),
    socials: jsonb().default({}),
    description: text(),
    google_place_id: text(),
    google_rating: numeric({ precision: 2, scale: 1 }),
    google_review_count: integer(),
    google_rating_fetched_at: timestamp({ withTimezone: true, mode: "string" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    group_restaurants_ids: bigint({ mode: "number" }).array(),
    shop_group_id: bigint({ mode: "number" }),
  },
  (table) => [
    index("restaurants_shop_group_id_idx").using(
      "btree",
      table.shop_group_id.asc().nullsLast().op("int8_ops"),
    ),
    uniqueIndex("restaurants_slug_unique_idx").using(
      "btree",
      table.slug.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const review_request_log = pgTable(
  "review_request_log",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: integer().notNull(),
    reservation_id: uuid().notNull(),
    customer_email: text().notNull(),
    sent_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    google_place_id: text(),
  },
  (table) => [
    index("review_request_log_restaurant_email_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.customer_email.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const service_to_service_type = pgTable("service_to_service_type", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "service_to_service_type_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  service_id: bigint({ mode: "number" }).notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  service_type_id: bigint({ mode: "number" }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const service_to_table = pgTable("service_to_table", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "service_to_table_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  table_id: integer().notNull(),
  service_id: integer().notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const service_types = pgTable("service_types", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "service_types_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  name: varchar({ length: 255 }).notNull(),
  alias: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const settings = pgTable("settings", {
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
    name: "settings_id_seq",
    startWith: 1,
    increment: 1,
    minValue: 1,
    maxValue: 9223372036854775807,
    cache: 1,
  }),
  key: varchar({ length: 255 }).notNull(),
  value: varchar({ length: 255 }).notNull(),
  created_at: timestamp({ mode: "string" }),
  updated_at: timestamp({ mode: "string" }),
});

export const shifts = pgTable(
  "shifts",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: uuid().notNull(),
    scheduled_start: timestamp({ withTimezone: true, mode: "string" }),
    scheduled_end: timestamp({ withTimezone: true, mode: "string" }),
    start: timestamp({ withTimezone: true, mode: "string" }),
    end: timestamp({ withTimezone: true, mode: "string" }),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    user_id: bigint({ mode: "number" }),
  },
  (table) => [
    index("idx_shifts_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("uuid_ops"),
    ),
  ],
);

export const stored_translations = pgTable("stored_translations", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }).notNull(),
  type: text().notNull(),
  lang: text().notNull(),
  key: text().notNull(),
  value: text().notNull(),
});

export const tax_item_types = pgTable("tax_item_types", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  name: text().notNull(),
  description: text(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const tax_payments = pgTable(
  "tax_payments",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    payment_id: bigint({ mode: "number" }),
    tax_rate_id: uuid(),
    total_amount: integer(),
    tax_amount: integer(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    unique("tax_payments_payment_id_tax_rate_id_unique").on(
      table.payment_id,
      table.tax_rate_id,
    ),
  ],
);

export const tax_profiles = pgTable("tax_profiles", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  service_method: text(),
  product_item_type: text(),
  tax_name: text().notNull(),
  jurisdiction: text(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }),
});

export const tax_rates = pgTable("tax_rates", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  type: text().notNull(),
  name: text().notNull(),
  value: numeric().notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" }),
  created_at: timestamp({ withTimezone: true, mode: "string" }),
  valid_from: timestamp({ withTimezone: true, mode: "string" }).notNull(),
  valid_till: timestamp({ withTimezone: true, mode: "string" }),
  jurisdiction: text(),
  // You can use { mode: "bigint" } if numbers are exceeding js number limitations
  restaurant_id: bigint({ mode: "number" }),
});

export const telescope_entries = pgTable(
  "telescope_entries",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    sequence: bigint({ mode: "number" })
      .primaryKey()
      .generatedByDefaultAsIdentity({
        name: "telescope_entries_sequence_seq",
        startWith: 1,
        increment: 1,
        minValue: 1,
        maxValue: 9223372036854775807,
        cache: 1,
      }),
    uuid: char({ length: 36 }).notNull(),
    batch_id: char({ length: 36 }).notNull(),
    family_hash: varchar({ length: 255 }),
    should_display_on_index: smallint().default(1).notNull(),
    type: varchar({ length: 20 }).notNull(),
    content: text().notNull(),
    created_at: timestamp({ mode: "string" }),
  },
  (table) => [
    index("telescope_entries_batch_id_idx").using(
      "btree",
      table.batch_id.asc().nullsLast().op("bpchar_ops"),
    ),
    index("telescope_entries_created_at_idx").using(
      "btree",
      table.created_at.asc().nullsLast().op("timestamp_ops"),
    ),
    index("telescope_entries_family_hash_idx").using(
      "btree",
      table.family_hash.asc().nullsLast().op("text_ops"),
    ),
    uniqueIndex("telescope_entries_uuid_idx").using(
      "btree",
      table.uuid.asc().nullsLast().op("bpchar_ops"),
    ),
  ],
);

export const telescope_monitoring = pgTable("telescope_monitoring", {
  tag: varchar({ length: 255 }).primaryKey().notNull(),
});

export const temp_caches = pgTable("temp_caches", {
  id: uuid().defaultRandom().primaryKey().notNull(),
  key: text().notNull(),
  type: text().notNull(),
  value: text().notNull(),
  created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
});

export const roles = pgTable(
  "roles",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "roles_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    guard_name: varchar({ length: 255 }).notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
  },
  (table) => [
    uniqueIndex("roles_name_guard_name_idx").using(
      "btree",
      table.name.asc().nullsLast().op("text_ops"),
      table.guard_name.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const identities = pgTable(
  "identities",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "identities_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    name: text().notNull(),
    email: text().notNull(),
    password: text(),
    status: text().default("active").notNull(),
    default_user_id: bigint({ mode: "number" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("identities_guid_unique_idx").using(
      "btree",
      table.guid.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("identities_active_email_unique_idx")
      .using("btree", sql`lower(${table.email})`)
      .where(sql`${table.deleted_at} IS NULL`),
    check(
      "identities_status_check",
      sql`${table.status} IN ('pending', 'active', 'inactive')`,
    ),
  ],
);

export const services = pgTable(
  "services",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "services_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }).notNull(),
    days: json(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order: bigint({ mode: "number" }).notNull(),
    hide_unavailable: varchar({ length: 255 }),
    visible_only_to_staff: smallint(),
    status: varchar({ length: 255 }).notNull(),
    type: varchar({ length: 255 }).default("table-service").notNull(),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    description: text(),
    guid: uuid().defaultRandom().notNull(),
    category_ids: uuid().array(),
    color: text(),
  },
  (table) => [
    index("idx_services_active").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("text_ops"),
      table.status.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_services_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
    index("services_restaurant_id_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const users = pgTable(
  "users",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "users_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    name: varchar({ length: 255 }).notNull(),
    email: varchar({ length: 255 }),
    phone: varchar({ length: 255 }),
    status: varchar({ length: 255 }).notNull(),
    staff_type: varchar({ length: 255 }),
    order_source: json(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    restaurant_id: bigint({ mode: "number" }),
    print_receipt: smallint().default(0).notNull(),
    email_verified_at: timestamp({ mode: "string" }),
    password: varchar({ length: 255 }).notNull(),
    remember_token: varchar({ length: 100 }),
    created_at: timestamp({ mode: "string" }),
    updated_at: timestamp({ mode: "string" }),
    deleted_at: timestamp({ mode: "string" }),
    guid: uuid().defaultRandom().notNull(),
    login_pin_code: text(),
    pat_token: uuid().defaultRandom().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    shop_ids: bigint({ mode: "number" }).array(),
    pin: text(),
    identity_id: bigint({ mode: "number" }),
  },
  (table) => [
    index("users_identity_id_idx").using(
      "btree",
      table.identity_id.asc().nullsLast().op("int8_ops"),
    ),
    index("idx_users_restaurant_id").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
    uniqueIndex("users_active_identity_restaurant_unique_idx")
      .using(
        "btree",
        table.identity_id.asc().nullsLast().op("int8_ops"),
        table.restaurant_id.asc().nullsLast().op("int8_ops"),
      )
      .where(
        sql`${table.identity_id} IS NOT NULL AND ${table.deleted_at} IS NULL`,
      ),
  ],
);

export const shop_group_members = pgTable(
  "shop_group_members",
  {
    identity_id: bigint({ mode: "number" }).notNull(),
    shop_group_id: bigint({ mode: "number" }).notNull(),
    role: text().default("employee").notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({
      columns: [table.identity_id, table.shop_group_id],
      name: "shop_group_members_pkey",
    }),
    index("shop_group_members_group_role_idx").using(
      "btree",
      table.shop_group_id.asc().nullsLast().op("int8_ops"),
      table.role.asc().nullsLast().op("text_ops"),
    ),
    // One group per identity: an identity may be a member of at most one shop group.
    uniqueIndex("shop_group_members_identity_unique_idx").using(
      "btree",
      table.identity_id.asc().nullsLast().op("int8_ops"),
    ),
    check(
      "shop_group_members_role_check",
      sql`${table.role} IN ('admin', 'viewer', 'employee')`,
    ),
  ],
);

export const identity_tokens = pgTable(
  "identity_tokens",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "identity_tokens_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    token_hash: text().notNull(),
    identity_id: bigint({ mode: "number" }).notNull(),
    purpose: text().notNull(),
    invited_by_identity_id: bigint({ mode: "number" }),
    expires_at: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    used_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("identity_tokens_token_hash_unique_idx").using(
      "btree",
      table.token_hash.asc().nullsLast().op("text_ops"),
    ),
    index("identity_tokens_identity_purpose_created_idx").using(
      "btree",
      table.identity_id.asc().nullsLast().op("int8_ops"),
      table.purpose.asc().nullsLast().op("text_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    check(
      "identity_tokens_purpose_check",
      sql`${table.purpose} IN ('invite', 'magic_link')`,
    ),
  ],
);

export const login_handoff_tokens = pgTable(
  "login_handoff_tokens",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "login_handoff_tokens_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    token_hash: text().notNull(),
    identity_id: bigint({ mode: "number" }).notNull(),
    target_user_id: bigint({ mode: "number" }).notNull(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    used_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("login_handoff_tokens_token_hash_unique_idx").using(
      "btree",
      table.token_hash.asc().nullsLast().op("text_ops"),
    ),
    index("login_handoff_tokens_identity_created_idx").using(
      "btree",
      table.identity_id.asc().nullsLast().op("int8_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
  ],
);

export const campaigns = pgTable(
  "campaigns",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    shop_id: integer().notNull(),
    name: text().notNull(),
    promotion_id: uuid().notNull(),
    targeting: jsonb().notNull(),
    channels: text().array().default(["email"]).notNull(),
    message_template: jsonb().notNull(),
    coupon_expires_in_days: integer(),
    status: text().default("draft").notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("campaigns_shop_id_idx").using(
      "btree",
      table.shop_id.asc().nullsLast().op("int4_ops"),
    ),
    foreignKey({
      columns: [table.promotion_id],
      foreignColumns: [promotions.id],
      name: "campaigns_promotion_id_fkey",
    }),
  ],
);

export const campaign_coupons = pgTable(
  "campaign_coupons",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    campaign_id: uuid().notNull(),
    campaign_run_id: uuid().notNull(),
    customer_id: text().notNull(),
    code: text().notNull(),
    status: text().default("pending").notNull(),
    sent_at: timestamp({ withTimezone: true, mode: "string" }),
    redeemed_at: timestamp({ withTimezone: true, mode: "string" }),
    redeemed_order_id: text(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("campaign_coupons_campaign_run_id_idx").using(
      "btree",
      table.campaign_run_id.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("campaign_coupons_code_unique").using(
      "btree",
      table.code.asc().nullsLast().op("text_ops"),
    ),
    index("campaign_coupons_customer_campaign_idx").using(
      "btree",
      table.customer_id.asc().nullsLast().op("uuid_ops"),
      table.campaign_id.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.campaign_id],
      foreignColumns: [campaigns.id],
      name: "campaign_coupons_campaign_id_fkey",
    }),
    foreignKey({
      columns: [table.campaign_run_id],
      foreignColumns: [campaign_runs.id],
      name: "campaign_coupons_campaign_run_id_fkey",
    }),
  ],
);

export const campaign_runs = pgTable(
  "campaign_runs",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    campaign_id: uuid().notNull(),
    status: text().default("pending").notNull(),
    cloudflare_workflow_id: text(),
    total_targeted: integer(),
    total_sent: integer().default(0).notNull(),
    total_failed: integer().default(0).notNull(),
    triggered_by: text().default("manual").notNull(),
    triggered_by_user_id: text(),
    started_at: timestamp({ withTimezone: true, mode: "string" }),
    completed_at: timestamp({ withTimezone: true, mode: "string" }),
    error: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("campaign_runs_campaign_id_idx").using(
      "btree",
      table.campaign_id.asc().nullsLast().op("uuid_ops"),
    ),
    foreignKey({
      columns: [table.campaign_id],
      foreignColumns: [campaigns.id],
      name: "campaign_runs_campaign_id_fkey",
    }),
  ],
);

export const shop_automations = pgTable(
  "shop_automations",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    // Retained as the concrete event source for shop-owned automations. A
    // group-owned automation has no single shop and therefore leaves this null.
    shop_id: bigint({ mode: "number" }),
    business_unit_id: bigint({ mode: "number" })
      .notNull()
      .references(() => business_units.id, { onDelete: "restrict" }),
    name: text().notNull(),
    trigger_event: text().notNull(),
    delay_minutes: integer().default(0).notNull(),
    channels: text().array().default(["email"]).notNull(),
    message_template: jsonb().notNull(),
    conditions: jsonb().default([]).notNull(),
    review_link: text(),
    // The list (membership program) a `member_created` automation targets:
    // when someone is added to this program, the automation fires. NULL for
    // every other trigger event (reservation/order/birthday).
    membership_program_id: uuid(),
    enabled: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
    updated_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("shop_automations_business_unit_id_idx").on(table.business_unit_id),
    index("shop_automations_membership_program_id_idx")
      .on(table.membership_program_id)
      .where(sql`${table.membership_program_id} IS NOT NULL`),
    foreignKey({
      columns: [table.membership_program_id, table.business_unit_id],
      foreignColumns: [
        membership_programs.id,
        membership_programs.business_unit_id,
      ],
      name: "shop_automations_membership_program_owner_fkey",
    }).onDelete("cascade"),
  ],
);

export const shop_automation_sends = pgTable(
  "shop_automation_sends",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    automation_id: uuid().notNull(),
    customer_id: text().notNull(),
    trigger_source_id: text().notNull(),
    trigger_source_type: text().notNull(),
    status: text().default("pending").notNull(),
    scheduled_for: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    sent_at: timestamp({ withTimezone: true, mode: "string" }),
    error: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" }).defaultNow(),
  },
  (table) => [
    index("shop_automation_sends_automation_id_idx").using(
      "btree",
      table.automation_id.asc().nullsLast().op("uuid_ops"),
    ),
    uniqueIndex("shop_automation_sends_idempotency_idx").using(
      "btree",
      table.automation_id.asc().nullsLast().op("text_ops"),
      table.trigger_source_id.asc().nullsLast().op("text_ops"),
    ),
    index("shop_automation_sends_status_scheduled_idx").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
      table.scheduled_for.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.automation_id],
      foreignColumns: [shop_automations.id],
      name: "shop_automation_sends_automation_id_fkey",
    }),
  ],
);

export const gift_cards = pgTable(
  "gift_cards",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    restaurant_id: integer().notNull(),
    code: text().notNull(),
    initial_value: numeric({ precision: 10, scale: 2 }).notNull(),
    current_balance: numeric({ precision: 10, scale: 2 }).notNull(),
    status: text().default("active").notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_id: bigint({ mode: "number" }),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    order_item_id: bigint({ mode: "number" }),
    customer_id: uuid(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    // 'gift_card' | 'table_credit'. A table credit is prepaid credit bound to
    // one table for one business day (docs/plans/table_credit).
    kind: text().default("gift_card").notNull(),
    table_id: integer(),
    business_date: date({ mode: "string" }),
    // The successful payment that funded a table credit.
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    payment_id: bigint({ mode: "number" }),
    // Set when the table holding the credit is closed (or the sweep settles
    // it); a closed credit no longer makes its table "credited".
    table_closed_at: timestamp({ withTimezone: true, mode: "string" }),
    // Who a plain gift card was sold to, taken at the POS and printed on the
    // receipt. Optional; never set for table credit.
    holder_name: text(),
    holder_email: text(),
    holder_phone: text(),
    /** When the card was emailed to holder_email (send-gift-card-email). */
    emailed_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("gift_cards_table_credit_open_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int4_ops"),
        table.table_id.asc().nullsLast().op("int4_ops"),
        table.created_at.asc().nullsLast().op("timestamptz_ops"),
      )
      .where(
        sql`((kind = 'table_credit'::text) AND (table_closed_at IS NULL))`,
      ),
    check(
      "gift_cards_kind_check",
      sql`kind = ANY (ARRAY['gift_card'::text, 'table_credit'::text])`,
    ),
    check(
      "gift_cards_table_credit_shape",
      sql`(kind <> 'table_credit'::text) OR ((table_id IS NOT NULL) AND ((status = 'pending'::text) OR ((business_date IS NOT NULL) AND (expires_at IS NOT NULL) AND (payment_id IS NOT NULL))))`,
    ),
    uniqueIndex("gift_cards_code_restaurant_idx").using(
      "btree",
      table.code.asc().nullsLast().op("text_ops"),
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    index("gift_cards_restaurant_id_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    index("gift_cards_status_idx").using(
      "btree",
      table.status.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const gift_card_transactions = pgTable(
  "gift_card_transactions",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    gift_card_id: uuid().notNull(),
    restaurant_id: integer().notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    payment_id: bigint({ mode: "number" }),
    type: text().notNull(),
    amount: numeric({ precision: 10, scale: 2 }).notNull(),
    balance_before: numeric({ precision: 10, scale: 2 }).notNull(),
    balance_after: numeric({ precision: 10, scale: 2 }).notNull(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    note: text(),
  },
  (table) => [
    index("gift_card_transactions_gift_card_id_idx").using(
      "btree",
      table.gift_card_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("gift_card_transactions_restaurant_id_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    foreignKey({
      columns: [table.gift_card_id],
      foreignColumns: [gift_cards.id],
      name: "gift_card_transactions_gift_card_id_fkey",
    }),
  ],
);

export const role_has_permissions = pgTable(
  "role_has_permissions",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    permission_id: bigint({ mode: "number" }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    role_id: bigint({ mode: "number" }).notNull(),
  },
  (table) => [
    index("role_has_permissions_role_id_idx").using(
      "btree",
      table.role_id.asc().nullsLast().op("int8_ops"),
    ),
    primaryKey({
      columns: [table.permission_id, table.role_id],
      name: "role_has_permissions_pkey",
    }),
  ],
);

export const telescope_entries_tags = pgTable(
  "telescope_entries_tags",
  {
    entry_uuid: char({ length: 36 }).notNull(),
    tag: varchar({ length: 255 }).notNull(),
  },
  (table) => [
    index("telescope_entries_tags_tag_idx").using(
      "btree",
      table.tag.asc().nullsLast().op("text_ops"),
    ),
    primaryKey({
      columns: [table.entry_uuid, table.tag],
      name: "telescope_entries_tags_pkey",
    }),
  ],
);

export const model_has_permissions = pgTable(
  "model_has_permissions",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    permission_id: bigint({ mode: "number" }).notNull(),
    model_type: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    model_id: bigint({ mode: "number" }).notNull(),
  },
  (table) => [
    index("model_has_permissions_model_id_model_type_idx").using(
      "btree",
      table.model_id.asc().nullsLast().op("text_ops"),
      table.model_type.asc().nullsLast().op("text_ops"),
    ),
    primaryKey({
      columns: [table.model_id, table.model_type, table.permission_id],
      name: "model_has_permissions_pkey",
    }),
  ],
);

export const model_has_roles = pgTable(
  "model_has_roles",
  {
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    role_id: bigint({ mode: "number" }).notNull(),
    model_type: varchar({ length: 255 }).notNull(),
    // You can use { mode: "bigint" } if numbers are exceeding js number limitations
    model_id: bigint({ mode: "number" }).notNull(),
  },
  (table) => [
    index("model_has_roles_model_id_model_type_idx").using(
      "btree",
      table.model_id.asc().nullsLast().op("text_ops"),
      table.model_type.asc().nullsLast().op("text_ops"),
    ),
    foreignKey({
      columns: [table.role_id],
      foreignColumns: [roles.id],
      name: "model_has_roles_role_id_fkey",
    }).onDelete("cascade"),
    primaryKey({
      columns: [table.model_id, table.model_type, table.role_id],
      name: "model_has_roles_pkey",
    }),
  ],
);

// ─── Accounting ───────────────────────────────────────────────────────────────

export const accounting_accounts = pgTable(
  "accounting_accounts",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity({
      name: "accounting_accounts_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    restaurant_id: bigint({ mode: "number" }).notNull(),
    code: text().notNull(),
    name: text().notNull(),
    kind: text().notNull(),
    category: text().notNull(),
    is_active: boolean().default(true).notNull(),
    metadata: jsonb().default({}).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    unique("accounting_accounts_restaurant_id_code_key").on(
      table.restaurant_id,
      table.code,
    ),
    index("accounting_accounts_restaurant_id_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const accounting_account_bindings = pgTable(
  "accounting_account_bindings",
  {
    restaurant_id: bigint({ mode: "number" }).notNull(),
    binding_type: text().notNull(),
    binding_key: text().default("default").notNull(),
    account_code: text().notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({
      columns: [table.restaurant_id, table.binding_type, table.binding_key],
      name: "accounting_account_bindings_pkey",
    }),
  ],
);

export const accounting_journals = pgTable(
  "accounting_journals",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    restaurant_id: bigint({ mode: "number" }).notNull(),
    type: text().notNull(),
    source_type: text().notNull(),
    source_id: text().notNull(),
    idempotency_key: text().notNull(),
    currency: char({ length: 3 }),
    effective_at: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    business_date: date().notNull(),
    posted_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    shift_id: uuid(),
    reversal_of: bigint({ mode: "number" }),
    reason: text(),
    metadata: jsonb().default({}).notNull(),
  },
  (table) => [
    unique("accounting_journals_restaurant_id_id_unique").on(
      table.restaurant_id,
      table.id,
    ),
    unique("accounting_journals_restaurant_id_idempotency_key_key").on(
      table.restaurant_id,
      table.idempotency_key,
    ),
    index("accounting_journals_restaurant_effective_at_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
      table.effective_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("accounting_journals_source_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
      table.source_type.asc().nullsLast().op("text_ops"),
      table.source_id.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const accounting_journal_entries = pgTable(
  "accounting_journal_entries",
  {
    id: bigint({ mode: "number" }).generatedAlwaysAsIdentity({
      name: "accounting_journal_entries_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: 9223372036854775807,
      cache: 1,
    }),
    journal_id: bigint({ mode: "number" }).notNull(),
    restaurant_id: bigint({ mode: "number" }).notNull(),
    entry_number: integer().notNull(),
    account_code: text().notNull(),
    amount: numeric({ precision: 18, scale: 4 }).notNull(),
    type: text().notNull(),
    payment_id: bigint({ mode: "number" }),
    payment_transaction_id: uuid(),
    order_id: bigint({ mode: "number" }),
    order_item_id: bigint({ mode: "number" }),
    product_id: bigint({ mode: "number" }),
    component_kind: text(),
    component_sequence: integer(),
    bundle_id: bigint({ mode: "number" }),
    bundle_option_id: bigint({ mode: "number" }),
    component_entity_type: text(),
    component_entity_id: bigint({ mode: "number" }),
    component_snapshot_status: text(),
    gift_card_id: uuid(),
    discount_id: bigint({ mode: "number" }),
    discount_component_id: bigint({ mode: "number" }),
    promotion_id: text(),
    tax_id: uuid(),
    tender_type: text(),
    note: text(),
    description: text(),
    metadata: jsonb().default({}).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    // Deprecated: deployed to production (see drizzle/0118_curly_onslaught.sql)
    // but no longer written or queried anywhere -- reports use shift-based
    // scoping instead (see reports-v2/reports functions), and the ClickHouse
    // warehouse mirror (apps/warehouse) doesn't carry it either. Left in place
    // rather than dropped since a column drop is a separate, deliberate prod
    // migration, not a byproduct of removing its (now dead) callers.
    business_date: date(),
    // Denormalized from the parent journal's effective_at (same value the
    // ClickHouse mirror now carries -- see apps/warehouse/migrations for
    // the ClickHouse side of this pair). Unlike business_date, this one is
    // written going forward (accounting_post_journal.sql) and backfilled
    // for history -- see plans/accounting_journal_entries_effective_at.md.
    effective_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    primaryKey({
      columns: [table.id, table.created_at],
      name: "accounting_journal_entries_pkey",
    }),
    unique(
      "accounting_journal_entries_journal_id_entry_number_created_at_key",
    ).on(table.journal_id, table.entry_number, table.created_at),
    index("accounting_entries_restaurant_account_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
      table.account_code.asc().nullsLast().op("text_ops"),
    ),
    index("accounting_entries_restaurant_business_date_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
      table.business_date.asc().nullsLast().op("date_ops"),
    ),
    index("accounting_entries_restaurant_effective_at_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int8_ops"),
      table.effective_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("accounting_entries_order_item_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int8_ops"),
        table.order_item_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`order_item_id IS NOT NULL`),
    index("accounting_entries_payment_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int8_ops"),
        table.payment_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`payment_id IS NOT NULL`),
    index("accounting_entries_component_entity_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int8_ops"),
        table.component_entity_type.asc().nullsLast().op("text_ops"),
        table.component_entity_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`component_kind IS NOT NULL`),
    index("accounting_entries_bundle_option_idx")
      .using(
        "btree",
        table.restaurant_id.asc().nullsLast().op("int8_ops"),
        table.bundle_id.asc().nullsLast().op("int8_ops"),
        table.bundle_option_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`component_kind = 'bundle_selection'`),
  ],
);

export const accounting_posting_failures = pgTable(
  "accounting_posting_failures",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    restaurant_id: bigint({ mode: "number" }).notNull(),
    payment_id: bigint({ mode: "number" }).notNull(),
    builder: text().notNull(),
    error: text().notNull(),
    attempts: integer().default(1).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    resolved_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    unique("accounting_posting_failures_payment_builder_key").on(
      table.payment_id,
      table.builder,
    ),
    index("accounting_posting_failures_unresolved_idx")
      .on(table.updated_at)
      .where(sql`resolved_at IS NULL`),
  ],
);

export const provider_webhook_events = pgTable(
  "provider_webhook_events",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedAlwaysAsIdentity(),
    restaurant_id: bigint({ mode: "number" }),
    provider: text().notNull(),
    event_type: text(),
    dedup_key: text().notNull(),
    payload: jsonb().notNull(),
    processed_at: timestamp({ withTimezone: true, mode: "string" }),
    error: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    unique("provider_webhook_events_provider_dedup_key").on(
      table.provider,
      table.dedup_key,
    ),
  ],
);

// provider_payouts, provider_payout_lines, and payment_provider_details were
// retired by the payout rework. Provider settlement evidence now lives in the raw
// `provider_webhook_events` payload plus the posted settlement journals. Payment
// resolution uses payment_transactions.providerId. See
// docs/architecture/accounting/payouts_and_reconciliation.md.

// ---------------------------------------------------------------------------
// MCP server (docs/architecture/intelligence/mcp.md)
//
// Four tables back the MCP authorization story. `mcp_oauth_clients` holds
// dynamic client registrations (RFC 7591); `mcp_grants` is the consent a
// merchant gave, at identity grain; `mcp_oauth_codes` is the short-lived
// authorization-code leg; `mcp_tokens` are the bearer credentials issued
// against a grant. Tokens are stored as sha256 hex, never in the clear, the
// same way identity_tokens and login_handoff_tokens do it.
// ---------------------------------------------------------------------------

export const mcp_oauth_clients = pgTable(
  "mcp_oauth_clients",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "mcp_oauth_clients_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    client_id: uuid().defaultRandom().notNull(),
    client_name: text(),
    redirect_uris: text().array().notNull(),
    // Public clients (PKCE, no secret) are the norm for MCP; the column stays
    // nullable so a confidential client can be registered later.
    client_secret_hash: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("mcp_oauth_clients_client_id_unique_idx").using(
      "btree",
      table.client_id.asc().nullsLast().op("uuid_ops"),
    ),
  ],
);

export const mcp_grants = pgTable(
  "mcp_grants",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "mcp_grants_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    identity_id: bigint({ mode: "number" }).notNull(),
    // The group the identity belonged to at consent time. Compared on every
    // call: shop_group_member_add can move an identity between groups, and a
    // grant must not follow it.
    shop_group_id: bigint({ mode: "number" }).notNull(),
    // The subset of group shops the merchant ticked. Narrows, never widens,
    // what manager_shops_list reports as live.
    shop_ids: bigint({ mode: "number" }).array().notNull(),
    access: text().default("read").notNull(),
    // Null for the dev-token path, which has no OAuth client.
    client_id: uuid(),
    label: text(),
    revoked_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("mcp_grants_guid_unique_idx").using(
      "btree",
      table.guid.asc().nullsLast().op("uuid_ops"),
    ),
    index("mcp_grants_identity_idx").using(
      "btree",
      table.identity_id.asc().nullsLast().op("int8_ops"),
    ),
    check(
      "mcp_grants_access_check",
      sql`${table.access} IN ('read', 'read_write')`,
    ),
  ],
);

export const mcp_oauth_codes = pgTable(
  "mcp_oauth_codes",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "mcp_oauth_codes_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    code_hash: text().notNull(),
    grant_id: bigint({ mode: "number" }).notNull(),
    client_id: uuid().notNull(),
    redirect_uri: text().notNull(),
    // PKCE (RFC 7636). S256 only — the plain method is not accepted.
    code_challenge: text().notNull(),
    resource: text(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    used_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("mcp_oauth_codes_code_hash_unique_idx").using(
      "btree",
      table.code_hash.asc().nullsLast().op("text_ops"),
    ),
  ],
);

export const mcp_tokens = pgTable(
  "mcp_tokens",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "mcp_tokens_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    token_hash: text().notNull(),
    grant_id: bigint({ mode: "number" }).notNull(),
    kind: text().notNull(),
    expires_at: timestamp({ withTimezone: true, mode: "string" }),
    revoked_at: timestamp({ withTimezone: true, mode: "string" }),
    last_used_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("mcp_tokens_token_hash_unique_idx").using(
      "btree",
      table.token_hash.asc().nullsLast().op("text_ops"),
    ),
    index("mcp_tokens_grant_idx").using(
      "btree",
      table.grant_id.asc().nullsLast().op("int8_ops"),
    ),
    check("mcp_tokens_kind_check", sql`${table.kind} IN ('access', 'refresh')`),
  ],
);

/**
 * Merchant-authored wallet pass designs.
 *
 * `document` is the canonical definition of a pass across Apple Wallet, Google
 * Wallet and the Collected browser card, validated by `walletPassDesignSchema`
 * in @lemmonhq/blogic/wallet-design. A design is live when a membership or coin
 * program selects it; an unselected design is a draft. There is no enabled or
 * default flag. See docs/plans/wallet_passes/08-canonical-pass-designs.md.
 */
export const wallet_pass_designs = pgTable(
  "wallet_pass_designs",
  {
    id: uuid().defaultRandom().primaryKey().notNull(),
    business_unit_id: bigint({ mode: "number" })
      .notNull()
      .references(() => business_units.id, { onDelete: "restrict" }),
    kind: text().notNull(),
    name: text().notNull(),
    document: jsonb().notNull(),
    /** Bumped on every save; issued passes record the revision they rendered. */
    revision: integer().default(1).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("wallet_pass_designs_lookup_idx").using(
      "btree",
      table.business_unit_id.asc().nullsLast().op("int8_ops"),
      table.kind.asc().nullsLast().op("text_ops"),
    ),
    unique("wallet_pass_designs_id_business_unit_id_unique").on(
      table.id,
      table.business_unit_id,
    ),
    // Target of the programs' (design, owner, kind) foreign keys.
    unique("wallet_pass_designs_id_business_unit_id_kind_unique").on(
      table.id,
      table.business_unit_id,
      table.kind,
    ),
    check(
      "wallet_pass_designs_kind_check",
      sql`${table.kind} IN ('membership', 'reservation', 'gift_card', 'coin')`,
    ),
    // Structural guard; field-level validation is the shared zod schema.
    check(
      "wallet_pass_designs_document_shape_check",
      sql`jsonb_typeof(${table.document}) = 'object' AND ${table.document} ?& ARRAY['brand', 'content', 'apple', 'google', 'browser'] AND (${table.kind} = 'coin' OR NOT ${table.document} ? 'stamps')`,
    ),
  ],
);

// ─── Inventory & costing ────────────────────────────────────────────────────
// See docs/plans/inventory_and_costing/03_data_model.md.
//
// Stock is held in each item's smallest unit (piece, ml or g). Cases, bottles
// and "1 egg = 60 g" are conversions in stock_item_units. Every change to
// stock is an append-only row in stock_movements; stock_levels is a cache of
// their sum. Purchased goods never live in `products`.

export const stock_items = pgTable(
  "stock_items",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "stock_items_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    name: text().notNull(),
    // piece (count), ml (volume) or g (mass). Fixes which conversions apply.
    base_unit: text().notNull(),
    stock_category: text(),
    // Cost per base unit. avg_cost (weighted average) is what sales are
    // costed at; last_cost (latest price paid) is shown as plate cost.
    avg_cost: numeric({ precision: 14, scale: 6 }).default("0").notNull(),
    last_cost: numeric({ precision: 14, scale: 6 }),
    par_level: numeric({ precision: 14, scale: 3 }),
    allergens: text()
      .array()
      .default(sql`'{}'::text[]`)
      .notNull(),
    nutrition: jsonb(),
    active: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("stock_items_guid_unique_idx").using(
      "btree",
      table.guid.asc().nullsLast().op("uuid_ops"),
    ),
    index("stock_items_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    check(
      "stock_items_base_unit_check",
      sql`${table.base_unit} IN ('piece', 'ml', 'g')`,
    ),
  ],
);

export const stock_item_units = pgTable(
  "stock_item_units",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "stock_item_units_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "cascade" }),
    // "case", "bottle", "egg" — how many base units one of these is.
    name: text().notNull(),
    base_qty: numeric({ precision: 14, scale: 6 }).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    unique("stock_item_units_item_name_unique").on(
      table.stock_item_id,
      table.name,
    ),
    check("stock_item_units_base_qty_check", sql`${table.base_qty} > 0`),
  ],
);

export const stock_movements = pgTable(
  "stock_movements",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "stock_movements_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "restrict" }),
    // Signed, in the item's base unit: + adds stock, - removes it.
    qty_delta: numeric({ precision: 14, scale: 3 }).notNull(),
    unit_cost: numeric({ precision: 14, scale: 6 }).default("0").notNull(),
    total_cost: numeric({ precision: 14, scale: 4 }).default("0").notNull(),
    type: text().notNull(),
    // What caused it: a receipt line, order item, count, waste entry, …
    source_type: text(),
    source_id: bigint({ mode: "number" }),
    occurred_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("stock_movements_item_time_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.stock_item_id.asc().nullsLast().op("int8_ops"),
      table.occurred_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("stock_movements_source_idx").using(
      "btree",
      table.source_type.asc().nullsLast().op("text_ops"),
      table.source_id.asc().nullsLast().op("int8_ops"),
    ),
    check(
      "stock_movements_type_check",
      sql`${table.type} IN ('receipt', 'sale', 'sale_reversal', 'waste', 'count_adjustment', 'transfer_in', 'transfer_out', 'production_in', 'production_out')`,
    ),
  ],
);

export const stock_levels = pgTable(
  "stock_levels",
  {
    stock_item_id: bigint({ mode: "number" })
      .primaryKey()
      .references(() => stock_items.id, { onDelete: "cascade" }),
    restaurant_id: integer().notNull(),
    qty_on_hand: numeric({ precision: 14, scale: 3 }).default("0").notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("stock_levels_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const recipes = pgTable(
  "recipes",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "recipes_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    // What the recipe is for: a product, or an extra (applied on top of the
    // product it's chosen with). A bundle option uses the recipe of the
    // product or extra it points at.
    entity_type: text().notNull(),
    entity_id: bigint({ mode: "number" }).notNull(),
    // A tray of lasagne yields 8 portions; each sale uses 1/8 of the lines.
    yield_portions: numeric({ precision: 10, scale: 3 }).default("1").notNull(),
    active: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("recipes_active_entity_unique_idx")
      .using(
        "btree",
        table.entity_type.asc().nullsLast().op("text_ops"),
        table.entity_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`${table.active}`),
    index("recipes_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    check(
      "recipes_entity_type_check",
      sql`${table.entity_type} IN ('product', 'extra')`,
    ),
    check("recipes_yield_check", sql`${table.yield_portions} > 0`),
  ],
);

export const recipe_lines = pgTable(
  "recipe_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "recipe_lines_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    recipe_id: bigint({ mode: "number" })
      .notNull()
      .references(() => recipes.id, { onDelete: "cascade" }),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "restrict" }),
    // Net quantity in `unit`: the item's base unit, a fixed conversion of it
    // (kg, l, cl), or one of its stock_item_units ("bottle", "egg").
    net_qty: numeric({ precision: 14, scale: 3 }).notNull(),
    unit: text().notNull(),
    // Expected trim/prep/pour loss. Stock used = net ÷ (1 − loss_pct/100).
    loss_pct: numeric({ precision: 5, scale: 2 }).default("0").notNull(),
    position: integer().default(0).notNull(),
  },
  (table) => [
    index("recipe_lines_recipe_idx").using(
      "btree",
      table.recipe_id.asc().nullsLast().op("int8_ops"),
    ),
    check("recipe_lines_net_qty_check", sql`${table.net_qty} > 0`),
    check(
      "recipe_lines_loss_pct_check",
      sql`${table.loss_pct} >= 0 AND ${table.loss_pct} < 100`,
    ),
  ],
);

// Cost of what each payment recognised, per order item: the stock it used at
// average cost, snapshotted when the payment's sale journal is posted. One
// live row per (payment, order item); a void reverses it (reversed_at) and its
// stock movements. See docs/plans/inventory_and_costing/04_costing.md.
export const order_item_costs = pgTable(
  "order_item_costs",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "order_item_costs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    payment_id: bigint({ mode: "number" }).notNull(),
    order_item_id: bigint({ mode: "number" }).notNull(),
    // Parent quantity of the item this payment recognised.
    quantity: numeric({ precision: 12, scale: 4 }).notNull(),
    cost_total: numeric({ precision: 14, scale: 4 }).default("0").notNull(),
    // False when neither the item nor its chosen options have a recipe —
    // lets reports show how much of revenue is costed.
    has_recipe: boolean().default(false).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    reversed_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("order_item_costs_live_unique_idx")
      .using(
        "btree",
        table.payment_id.asc().nullsLast().op("int8_ops"),
        table.order_item_id.asc().nullsLast().op("int8_ops"),
      )
      .where(sql`${table.reversed_at} IS NULL`),
    index("order_item_costs_restaurant_created_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("order_item_costs_order_item_idx").using(
      "btree",
      table.order_item_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

// ─── Purchasing ─────────────────────────────────────────────────────────────
// See docs/plans/inventory_and_costing/05_purchasing.md. Buyer-side records:
// who a shop buys from, what it buys from them (and at what price over time),
// and what actually arrived. Posting a goods receipt adds stock.

export const suppliers = pgTable(
  "suppliers",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "suppliers_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    // Set when the supplier is another Lemmon shop (marketplace).
    supplier_restaurant_id: integer(),
    name: text().notNull(),
    vat_number: text(),
    email: text(),
    phone: text(),
    address: text(),
    notes: text(),
    active: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("suppliers_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const supplier_items = pgTable(
  "supplier_items",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "supplier_items_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    supplier_id: bigint({ mode: "number" })
      .notNull()
      .references(() => suppliers.id, { onDelete: "cascade" }),
    // As printed on the supplier's invoice; earlier spellings in aliases.
    name: text().notNull(),
    aliases: text()
      .array()
      .default(sql`'{}'::text[]`)
      .notNull(),
    supplier_code: text(),
    gtin: text(),
    // What one invoiced unit is in my stock: stock item + a unit it converts
    // from ("case" of a lager, "bottle" of a wine).
    stock_item_id: bigint({ mode: "number" }).references(() => stock_items.id, {
      onDelete: "set null",
    }),
    unit: text(),
    last_price: numeric({ precision: 14, scale: 4 }),
    active: boolean().default(true).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("supplier_items_supplier_idx").using(
      "btree",
      table.supplier_id.asc().nullsLast().op("int8_ops"),
    ),
    index("supplier_items_stock_item_idx").using(
      "btree",
      table.stock_item_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const supplier_item_prices = pgTable(
  "supplier_item_prices",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "supplier_item_prices_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    supplier_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => supplier_items.id, { onDelete: "cascade" }),
    // Price per supplier_items.unit, as invoiced.
    price: numeric({ precision: 14, scale: 4 }).notNull(),
    source_type: text(),
    source_id: bigint({ mode: "number" }),
    recorded_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("supplier_item_prices_item_time_idx").using(
      "btree",
      table.supplier_item_id.asc().nullsLast().op("int8_ops"),
      table.recorded_at.asc().nullsLast().op("timestamptz_ops"),
    ),
  ],
);

export const goods_receipts = pgTable(
  "goods_receipts",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "goods_receipts_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    supplier_id: bigint({ mode: "number" }).references(() => suppliers.id, {
      onDelete: "set null",
    }),
    // Delivery note or invoice number.
    reference: text(),
    received_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    // draft → posted. Posting adds stock and can't be undone (post a
    // correcting count or waste instead).
    status: text().default("draft").notNull(),
    notes: text(),
    // Uploaded invoice/delivery note (R2 key), for scanning and the record.
    document_key: text(),
    total: numeric({ precision: 14, scale: 2 }).default("0").notNull(),
    // The purchase order this delivery fulfils.
    purchase_order_id: bigint({ mode: "number" }).references(
      (): AnyPgColumn => purchase_orders.id,
      { onDelete: "set null" },
    ),
    // The supplier invoice billing this delivery.
    supplier_invoice_id: bigint({ mode: "number" }).references(
      (): AnyPgColumn => supplier_invoices.id,
      { onDelete: "set null" },
    ),
    created_by: integer(),
    posted_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("goods_receipts_restaurant_received_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.received_at.desc().nullsFirst().op("timestamptz_ops"),
    ),
    check(
      "goods_receipts_status_check",
      sql`${table.status} IN ('draft', 'posted')`,
    ),
  ],
);

export const goods_receipt_lines = pgTable(
  "goods_receipt_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "goods_receipt_lines_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    receipt_id: bigint({ mode: "number" })
      .notNull()
      .references(() => goods_receipts.id, { onDelete: "cascade" }),
    supplier_item_id: bigint({ mode: "number" }).references(
      () => supplier_items.id,
      { onDelete: "set null" },
    ),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "restrict" }),
    description: text(),
    // As on the supplier's invoice, carried to the supplier item on posting
    // so later invoices match by code.
    supplier_code: text(),
    gtin: text(),
    // In `unit` (any unit that converts to the stock item's base unit).
    quantity: numeric({ precision: 14, scale: 3 }).notNull(),
    unit: text().notNull(),
    // Price per `unit`, excluding VAT.
    unit_price: numeric({ precision: 14, scale: 4 }).notNull(),
    // Set when a manager accepts a price well above the last one paid.
    price_confirmed: boolean().default(false).notNull(),
    position: integer().default(0).notNull(),
  },
  (table) => [
    index("goods_receipt_lines_receipt_idx").using(
      "btree",
      table.receipt_id.asc().nullsLast().op("int8_ops"),
    ),
    check("goods_receipt_lines_quantity_check", sql`${table.quantity} > 0`),
    check(
      "goods_receipt_lines_unit_price_check",
      sql`${table.unit_price} >= 0`,
    ),
  ],
);

// Waste: stock thrown away, broken, eaten by staff or given away. An entry is
// either a stock item, or a whole dish (product) whose recipe is used up.
// Its stock movements have source_type 'waste_entry'.
// See docs/plans/inventory_and_costing/06_capture.md.
export const waste_entries = pgTable(
  "waste_entries",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "waste_entries_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    stock_item_id: bigint({ mode: "number" }).references(() => stock_items.id, {
      onDelete: "restrict",
    }),
    product_id: integer(),
    quantity: numeric({ precision: 14, scale: 3 }).notNull(),
    // Unit of `quantity` for a stock item; portions for a dish.
    unit: text(),
    reason: text().notNull(),
    note: text(),
    cost_total: numeric({ precision: 14, scale: 4 }).default("0").notNull(),
    occurred_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("waste_entries_restaurant_time_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.occurred_at.desc().nullsFirst().op("timestamptz_ops"),
    ),
    check(
      "waste_entries_target_check",
      sql`(${table.stock_item_id} IS NOT NULL) <> (${table.product_id} IS NOT NULL)`,
    ),
    check(
      "waste_entries_reason_check",
      sql`${table.reason} IN ('spoiled', 'expired', 'broken', 'staff_meal', 'comp', 'returned', 'other')`,
    ),
    check("waste_entries_quantity_check", sql`${table.quantity} > 0`),
  ],
);

// Stock counts: what staff physically counted. Posting writes a
// count_adjustment movement for each line's difference from the expected
// level; the first count of an item is its opening balance.
// See docs/plans/inventory_and_costing/06_capture.md.
export const stock_counts = pgTable(
  "stock_counts",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "stock_counts_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    name: text(),
    status: text().default("draft").notNull(),
    notes: text(),
    counted_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    created_by: integer(),
    posted_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("stock_counts_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.counted_at.desc().nullsFirst().op("timestamptz_ops"),
    ),
    check(
      "stock_counts_status_check",
      sql`${table.status} IN ('draft', 'posted')`,
    ),
  ],
);

export const stock_count_lines = pgTable(
  "stock_count_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "stock_count_lines_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    count_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_counts.id, { onDelete: "cascade" }),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "restrict" }),
    // As entered, e.g. [{"qty": 4, "unit": "bottle"}, {"qty": 300, "unit": "ml"}].
    entries: jsonb().notNull(),
    // Their sum in the item's base unit.
    counted_qty: numeric({ precision: 14, scale: 3 }).notNull(),
    // Filled when the count is posted.
    expected_qty: numeric({ precision: 14, scale: 3 }),
    difference: numeric({ precision: 14, scale: 3 }),
    difference_value: numeric({ precision: 14, scale: 4 }),
  },
  (table) => [
    unique("stock_count_lines_count_item_unique").on(
      table.count_id,
      table.stock_item_id,
    ),
  ],
);

// Supplier invoices: what a shop owes its suppliers, and whether it's paid.
// Linked to the deliveries (goods receipts) they bill for. Overdue is derived
// (open and past due_date). See docs/plans/inventory_and_costing/05_purchasing.md.
export const supplier_invoices = pgTable(
  "supplier_invoices",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "supplier_invoices_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    supplier_id: bigint({ mode: "number" }).references(() => suppliers.id, {
      onDelete: "set null",
    }),
    invoice_number: text(),
    issue_date: date({ mode: "string" }).notNull(),
    due_date: date({ mode: "string" }),
    currency: text(),
    net_amount: numeric({ precision: 14, scale: 2 }),
    vat_amount: numeric({ precision: 14, scale: 2 }),
    total: numeric({ precision: 14, scale: 2 }).notNull(),
    // open → paid; void for a cancelled or duplicate invoice.
    status: text().default("open").notNull(),
    paid_at: timestamp({ withTimezone: true, mode: "string" }),
    paid_amount: numeric({ precision: 14, scale: 2 }),
    payment_method: text(),
    payment_reference: text(),
    notes: text(),
    // Uploaded invoice (R2 key).
    document_key: text(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("supplier_invoices_restaurant_issue_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.issue_date.desc().nullsFirst().op("date_ops"),
    ),
    check(
      "supplier_invoices_status_check",
      sql`${table.status} IN ('open', 'paid', 'void')`,
    ),
  ],
);

export const supplier_invoice_lines = pgTable(
  "supplier_invoice_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "supplier_invoice_lines_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    invoice_id: bigint({ mode: "number" })
      .notNull()
      .references(() => supplier_invoices.id, { onDelete: "cascade" }),
    description: text().notNull(),
    supplier_code: text(),
    quantity: numeric({ precision: 14, scale: 3 }),
    unit: text(),
    // Excluding VAT.
    unit_price: numeric({ precision: 14, scale: 4 }),
    vat_rate: numeric({ precision: 5, scale: 2 }),
    line_total: numeric({ precision: 14, scale: 2 }).notNull(),
    position: integer().default(0).notNull(),
  },
  (table) => [
    index("supplier_invoice_lines_invoice_idx").using(
      "btree",
      table.invoice_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

// Purchase orders to suppliers (outside Lemmon; marketplace orders live in the
// seller's `orders`). draft → sent → partially_received → received, or
// cancelled. Receiving happens through goods receipts linked by
// goods_receipts.purchase_order_id. See docs/plans/inventory_and_costing/05_purchasing.md.
export const purchase_orders = pgTable(
  "purchase_orders",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "purchase_orders_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    // Sequential per restaurant, shown as PO-0001.
    order_number: integer().notNull(),
    supplier_id: bigint({ mode: "number" }).references(() => suppliers.id, {
      onDelete: "set null",
    }),
    // The supplier's order confirmation number, if any.
    reference: text(),
    status: text().default("draft").notNull(),
    ordered_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    expected_at: date({ mode: "string" }),
    notes: text(),
    // Uploaded order confirmation (R2 key).
    document_key: text(),
    total: numeric({ precision: 14, scale: 2 }).default("0").notNull(),
    created_by: integer(),
    sent_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("purchase_orders_restaurant_number_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.order_number.asc().nullsLast().op("int4_ops"),
    ),
    check(
      "purchase_orders_status_check",
      sql`${table.status} IN ('draft', 'sent', 'partially_received', 'received', 'cancelled')`,
    ),
  ],
);

export const purchase_order_lines = pgTable(
  "purchase_order_lines",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "purchase_order_lines_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    purchase_order_id: bigint({ mode: "number" })
      .notNull()
      .references(() => purchase_orders.id, { onDelete: "cascade" }),
    supplier_item_id: bigint({ mode: "number" }).references(
      () => supplier_items.id,
      { onDelete: "set null" },
    ),
    stock_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => stock_items.id, { onDelete: "restrict" }),
    description: text(),
    supplier_code: text(),
    // In `unit` (any unit that converts to the stock item's base unit).
    quantity: numeric({ precision: 14, scale: 3 }).notNull(),
    unit: text().notNull(),
    // Expected price per `unit`, excluding VAT.
    unit_price: numeric({ precision: 14, scale: 4 }).default("0").notNull(),
    position: integer().default(0).notNull(),
  },
  (table) => [
    index("purchase_order_lines_order_idx").using(
      "btree",
      table.purchase_order_id.asc().nullsLast().op("int8_ops"),
    ),
    check("purchase_order_lines_quantity_check", sql`${table.quantity} > 0`),
  ],
);

// Work Day: recurring procedures (checklists, temperature logs), prep lists
// and one-off tasks, shown together on MyDay. Each day's run of a procedure
// or prep list is its own row, so history is kept.
// See docs/plans/workday/readme.md.
export const procedures = pgTable(
  "procedures",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "procedures_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    name: text().notNull(),
    description: text(),
    // kitchen, front_of_house, bar, other.
    department: text().default("kitchen").notNull(),
    // ISO weekdays it's due (1 = Monday … 7 = Sunday); empty = on demand.
    schedule_days: smallint()
      .array()
      .default(sql`'{}'::smallint[]`)
      .notNull(),
    // Local time it's due by, if any.
    schedule_time: time(),
    active: boolean().default(true).notNull(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("procedures_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const procedure_steps = pgTable(
  "procedure_steps",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "procedure_steps_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    procedure_id: bigint({ mode: "number" })
      .notNull()
      .references(() => procedures.id, { onDelete: "cascade" }),
    title: text().notNull(),
    // check, yes_no, temperature, number, text.
    kind: text().default("check").notNull(),
    required: boolean().default(true).notNull(),
    // Acceptable range for temperature / number answers.
    min_value: numeric({ precision: 10, scale: 2 }),
    max_value: numeric({ precision: 10, scale: 2 }),
    unit: text(),
    position: integer().default(0).notNull(),
    // Removed from the procedure; past answers keep pointing at it.
    archived_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("procedure_steps_procedure_idx").using(
      "btree",
      table.procedure_id.asc().nullsLast().op("int8_ops"),
    ),
    check(
      "procedure_steps_kind_check",
      sql`${table.kind} IN ('check', 'yes_no', 'temperature', 'number', 'text')`,
    ),
  ],
);

export const procedure_runs = pgTable(
  "procedure_runs",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "procedure_runs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    procedure_id: bigint({ mode: "number" })
      .notNull()
      .references(() => procedures.id, { onDelete: "cascade" }),
    run_date: date({ mode: "string" }).notNull(),
    status: text().default("in_progress").notNull(),
    // Answers outside their range, or "no" to a yes/no step.
    issues: integer().default(0).notNull(),
    notes: text(),
    started_by: integer(),
    completed_by: integer(),
    started_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    completed_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("procedure_runs_procedure_date_idx").using(
      "btree",
      table.procedure_id.asc().nullsLast().op("int8_ops"),
      table.run_date.asc().nullsLast().op("date_ops"),
    ),
    index("procedure_runs_restaurant_date_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.run_date.asc().nullsLast().op("date_ops"),
    ),
    check(
      "procedure_runs_status_check",
      sql`${table.status} IN ('in_progress', 'completed')`,
    ),
  ],
);

export const procedure_run_answers = pgTable(
  "procedure_run_answers",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "procedure_run_answers_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    run_id: bigint({ mode: "number" })
      .notNull()
      .references(() => procedure_runs.id, { onDelete: "cascade" }),
    step_id: bigint({ mode: "number" })
      .notNull()
      .references(() => procedure_steps.id, { onDelete: "cascade" }),
    checked: boolean(),
    value_number: numeric({ precision: 10, scale: 2 }),
    value_text: text(),
    is_issue: boolean().default(false).notNull(),
    answered_by: integer(),
    answered_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    uniqueIndex("procedure_run_answers_run_step_idx").using(
      "btree",
      table.run_id.asc().nullsLast().op("int8_ops"),
      table.step_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const prep_lists = pgTable(
  "prep_lists",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "prep_lists_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    name: text().notNull(),
    department: text().default("kitchen").notNull(),
    schedule_days: smallint()
      .array()
      .default(sql`'{}'::smallint[]`)
      .notNull(),
    active: boolean().default(true).notNull(),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("prep_lists_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const prep_items = pgTable(
  "prep_items",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "prep_items_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    prep_list_id: bigint({ mode: "number" })
      .notNull()
      .references(() => prep_lists.id, { onDelete: "cascade" }),
    name: text().notNull(),
    // How much should be ready at the start of service, in `unit`.
    par_qty: numeric({ precision: 10, scale: 2 }).default("0").notNull(),
    unit: text(),
    notes: text(),
    position: integer().default(0).notNull(),
    archived_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("prep_items_list_idx").using(
      "btree",
      table.prep_list_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const prep_runs = pgTable(
  "prep_runs",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "prep_runs_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    prep_list_id: bigint({ mode: "number" })
      .notNull()
      .references(() => prep_lists.id, { onDelete: "cascade" }),
    run_date: date({ mode: "string" }).notNull(),
    started_by: integer(),
    started_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    completed_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("prep_runs_list_date_idx").using(
      "btree",
      table.prep_list_id.asc().nullsLast().op("int8_ops"),
      table.run_date.asc().nullsLast().op("date_ops"),
    ),
    index("prep_runs_restaurant_date_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.run_date.asc().nullsLast().op("date_ops"),
    ),
  ],
);

export const prep_run_items = pgTable(
  "prep_run_items",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "prep_run_items_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    run_id: bigint({ mode: "number" })
      .notNull()
      .references(() => prep_runs.id, { onDelete: "cascade" }),
    prep_item_id: bigint({ mode: "number" })
      .notNull()
      .references(() => prep_items.id, { onDelete: "cascade" }),
    par_qty: numeric({ precision: 10, scale: 2 }).notNull(),
    on_hand: numeric({ precision: 10, scale: 2 }),
    // What to prepare: par − on hand by default, editable.
    to_prep: numeric({ precision: 10, scale: 2 }).notNull(),
    done: boolean().default(false).notNull(),
    done_by: integer(),
    done_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    uniqueIndex("prep_run_items_run_item_idx").using(
      "btree",
      table.run_id.asc().nullsLast().op("int8_ops"),
      table.prep_item_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const tasks = pgTable(
  "tasks",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "tasks_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    title: text().notNull(),
    notes: text(),
    department: text(),
    // users.id; null = anyone.
    assigned_to: integer(),
    due_date: date({ mode: "string" }),
    priority: text().default("normal").notNull(),
    status: text().default("open").notNull(),
    done_by: integer(),
    done_at: timestamp({ withTimezone: true, mode: "string" }),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("tasks_restaurant_status_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.status.asc().nullsLast().op("text_ops"),
    ),
    check("tasks_status_check", sql`${table.status} IN ('open', 'done')`),
    check("tasks_priority_check", sql`${table.priority} IN ('normal', 'high')`),
  ],
);

// Staff planning (rota): the people on a shop's rota, independent of POS
// `users` (optional link through user_id for clock-ins). Email is not unique —
// the same person can work in several shops. They reach their schedule
// through a personal link; only the SHA-256 of its token is stored.
// See docs/plans/workday/staff_planning.md.
export const staff = pgTable(
  "staff",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "staff_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    guid: uuid().defaultRandom().notNull(),
    restaurant_id: integer().notNull(),
    // POS user, if this person also uses the POS (matches clock-ins).
    user_id: integer(),
    name: text().notNull(),
    email: text(),
    phone: text(),
    color: text(),
    contract_hours_week: numeric({ precision: 5, scale: 2 }),
    // Managers only (see the rota router).
    hourly_rate: numeric({ precision: 10, scale: 2 }),
    notes: text(),
    active: boolean().default(true).notNull(),
    link_token_hash: text(),
    link_sent_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("staff_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
    uniqueIndex("staff_link_token_hash_idx")
      .using("btree", table.link_token_hash.asc().nullsLast().op("text_ops"))
      .where(sql`${table.link_token_hash} IS NOT NULL`),
  ],
);

// Competencies a shift can require: chef, floor, bar, barista, shift lead…
export const staff_roles = pgTable(
  "staff_roles",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "staff_roles_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    name: text().notNull(),
    color: text(),
    position: integer().default(0).notNull(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("staff_roles_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const staff_skills = pgTable(
  "staff_skills",
  {
    staff_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    role_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff_roles.id, { onDelete: "cascade" }),
  },
  (table) => [
    primaryKey({
      columns: [table.staff_id, table.role_id],
      name: "staff_skills_pkey",
    }),
    index("staff_skills_role_idx").using(
      "btree",
      table.role_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

// Labour-rule limits per shop. The rota only warns when a plan breaks them;
// every limit is editable so other countries can use their own.
export const rota_settings = pgTable("rota_settings", {
  restaurant_id: integer().primaryKey().notNull(),
  max_hours_day: numeric({ precision: 5, scale: 2 }).default("10").notNull(),
  max_hours_week: numeric({ precision: 5, scale: 2 }).default("45").notNull(),
  min_rest_hours: numeric({ precision: 5, scale: 2 }).default("11").notNull(),
  max_consecutive_days: integer().default(6).notNull(),
  publish_days_ahead: integer().default(14).notNull(),
  updated_at: timestamp({ withTimezone: true, mode: "string" })
    .defaultNow()
    .notNull(),
});

// A typical day ("Standard day", "Friday service"): timed blocks, each with
// the roles and headcount it needs. Weekdays get a template by default.
// See docs/plans/workday/staff_planning.md (phase 1b).
export const shift_templates = pgTable(
  "shift_templates",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "shift_templates_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    name: text().notNull(),
    // Two or three letters for compact views (ST, FR, SU).
    code: text(),
    color: text(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    deleted_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    index("shift_templates_restaurant_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
    ),
  ],
);

export const shift_template_blocks = pgTable(
  "shift_template_blocks",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "shift_template_blocks_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    template_id: bigint({ mode: "number" })
      .notNull()
      .references(() => shift_templates.id, { onDelete: "cascade" }),
    name: text(),
    start_time: time().notNull(),
    // At or before start_time means the block ends after midnight.
    end_time: time().notNull(),
    break_minutes: integer().default(0).notNull(),
    position: integer().default(0).notNull(),
  },
  (table) => [
    index("shift_template_blocks_template_idx").using(
      "btree",
      table.template_id.asc().nullsLast().op("int8_ops"),
    ),
  ],
);

export const shift_template_slots = pgTable(
  "shift_template_slots",
  {
    block_id: bigint({ mode: "number" })
      .notNull()
      .references(() => shift_template_blocks.id, { onDelete: "cascade" }),
    role_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff_roles.id, { onDelete: "cascade" }),
    headcount: integer().default(1).notNull(),
  },
  (table) => [
    primaryKey({
      columns: [table.block_id, table.role_id],
      name: "shift_template_slots_pkey",
    }),
    check("shift_template_slots_headcount_check", sql`${table.headcount} > 0`),
  ],
);

export const rota_weekday_templates = pgTable(
  "rota_weekday_templates",
  {
    restaurant_id: integer().notNull(),
    // ISO weekday, 1 = Monday … 7 = Sunday.
    weekday: smallint().notNull(),
    template_id: bigint({ mode: "number" })
      .notNull()
      .references(() => shift_templates.id, { onDelete: "cascade" }),
  },
  (table) => [
    primaryKey({
      columns: [table.restaurant_id, table.weekday],
      name: "rota_weekday_templates_pkey",
    }),
    check(
      "rota_weekday_templates_weekday_check",
      sql`${table.weekday} BETWEEN 1 AND 7`,
    ),
  ],
);

// A planned shift: one person (or a gap, staff_id null) in one role for a
// time on a date. Drafts stay private until a manager publishes them;
// published gaps are open shifts. Times are copied from the template, so
// template edits never change a rota. See docs/plans/workday/staff_planning.md.
export const rota_shifts = pgTable(
  "rota_shifts",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "rota_shifts_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    shift_date: date({ mode: "string" }).notNull(),
    start_time: time().notNull(),
    // At or before start_time means the shift ends the next day.
    end_time: time().notNull(),
    break_minutes: integer().default(0).notNull(),
    role_id: bigint({ mode: "number" }).references(() => staff_roles.id, {
      onDelete: "set null",
    }),
    staff_id: bigint({ mode: "number" }).references(() => staff.id, {
      onDelete: "set null",
    }),
    block_name: text(),
    // draft → published.
    status: text().default("draft").notNull(),
    // template, manual, auto.
    source: text().default("manual").notNull(),
    notes: text(),
    published_at: timestamp({ withTimezone: true, mode: "string" }),
    created_by: integer(),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("rota_shifts_restaurant_date_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.shift_date.asc().nullsLast().op("date_ops"),
    ),
    index("rota_shifts_staff_date_idx").using(
      "btree",
      table.staff_id.asc().nullsLast().op("int8_ops"),
      table.shift_date.asc().nullsLast().op("date_ops"),
    ),
    check(
      "rota_shifts_status_check",
      sql`${table.status} IN ('draft', 'published')`,
    ),
    check(
      "rota_shifts_source_check",
      sql`${table.source} IN ('template', 'manual', 'auto')`,
    ),
  ],
);

// A month the manager collects preferences for. Staff submit through their
// personal link while it's open. See docs/plans/workday/staff_planning.md (phase 2).
export const rota_preference_periods = pgTable(
  "rota_preference_periods",
  {
    restaurant_id: integer().notNull(),
    // First day of the month.
    month: date({ mode: "string" }).notNull(),
    status: text().default("open").notNull(),
    opened_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    closed_at: timestamp({ withTimezone: true, mode: "string" }),
  },
  (table) => [
    primaryKey({
      columns: [table.restaurant_id, table.month],
      name: "rota_preference_periods_pkey",
    }),
    check(
      "rota_preference_periods_status_check",
      sql`${table.status} IN ('open', 'closed')`,
    ),
  ],
);

// What a person would like to work in a month: target hours, weekdays and/or dates.
export const staff_availability = pgTable(
  "staff_availability",
  {
    staff_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    month: date({ mode: "string" }).notNull(),
    desired_hours: numeric({ precision: 6, scale: 2 }),
    // ISO weekdays they'd like to work (1–7).
    weekdays: smallint()
      .array()
      .default(sql`'{}'::smallint[]`)
      .notNull(),
    // Specific dates they'd like to work.
    dates: date({ mode: "string" })
      .array()
      .default(sql`'{}'::date[]`)
      .notNull(),
    note: text(),
    submitted_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    primaryKey({
      columns: [table.staff_id, table.month],
      name: "staff_availability_pkey",
    }),
  ],
);

// Time off a person asked for; approved time off is a warning on the rota.
export const staff_time_off = pgTable(
  "staff_time_off",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "staff_time_off_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    staff_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    start_date: date({ mode: "string" }).notNull(),
    end_date: date({ mode: "string" }).notNull(),
    reason: text(),
    // pending → approved / declined; cancelled by the person while pending.
    status: text().default("pending").notNull(),
    decided_by: integer(),
    decided_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("staff_time_off_restaurant_dates_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.start_date.asc().nullsLast().op("date_ops"),
    ),
    check(
      "staff_time_off_status_check",
      sql`${table.status} IN ('pending', 'approved', 'declined', 'cancelled')`,
    ),
    check(
      "staff_time_off_dates_check",
      sql`${table.end_date} >= ${table.start_date}`,
    ),
  ],
);

// After publishing: a team member asks to pick up an open shift ("claim") or
// to give one of theirs away ("offer"); a manager approves or declines.
// See docs/plans/workday/staff_planning.md (phase 4).
export const rota_shift_requests = pgTable(
  "rota_shift_requests",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "rota_shift_requests_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    shift_id: bigint({ mode: "number" })
      .notNull()
      .references(() => rota_shifts.id, { onDelete: "cascade" }),
    staff_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    kind: text().notNull(),
    // pending → approved / declined; cancelled by the person while pending.
    status: text().default("pending").notNull(),
    note: text(),
    decided_by: integer(),
    decided_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("rota_shift_requests_restaurant_status_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.status.asc().nullsLast().op("text_ops"),
    ),
    uniqueIndex("rota_shift_requests_one_pending_idx")
      .using(
        "btree",
        table.shift_id.asc().nullsLast().op("int8_ops"),
        table.staff_id.asc().nullsLast().op("int8_ops"),
        table.kind.asc().nullsLast().op("text_ops"),
      )
      .where(sql`${table.status} = 'pending'`),
    check(
      "rota_shift_requests_kind_check",
      sql`${table.kind} IN ('claim', 'offer')`,
    ),
    check(
      "rota_shift_requests_status_check",
      sql`${table.status} IN ('pending', 'approved', 'declined', 'cancelled')`,
    ),
  ],
);

// Worked time: clock-in/out from a person's personal link (or entered by a
// manager for a missed one), compared with their planned shifts. The POS
// `shifts` table is a business-day shift, not a time clock.
// See docs/plans/workday/staff_planning.md (phase 5).
export const staff_time_entries = pgTable(
  "staff_time_entries",
  {
    id: bigint({ mode: "number" }).primaryKey().generatedByDefaultAsIdentity({
      name: "staff_time_entries_id_seq",
      startWith: 1,
      increment: 1,
      minValue: 1,
      maxValue: "9223372036854775807",
      cache: 1,
    }),
    restaurant_id: integer().notNull(),
    staff_id: bigint({ mode: "number" })
      .notNull()
      .references(() => staff.id, { onDelete: "cascade" }),
    // The planned shift this covers, when there was one.
    shift_id: bigint({ mode: "number" }).references(() => rota_shifts.id, {
      onDelete: "set null",
    }),
    clock_in: timestamp({ withTimezone: true, mode: "string" }).notNull(),
    clock_out: timestamp({ withTimezone: true, mode: "string" }),
    // link (the person) or manager (backfill / correction).
    source: text().default("link").notNull(),
    note: text(),
    edited_by: integer(),
    edited_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
  },
  (table) => [
    index("staff_time_entries_restaurant_in_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.clock_in.asc().nullsLast().op("timestamptz_ops"),
    ),
    uniqueIndex("staff_time_entries_one_open_idx")
      .using("btree", table.staff_id.asc().nullsLast().op("int8_ops"))
      .where(sql`${table.clock_out} IS NULL`),
    check(
      "staff_time_entries_source_check",
      sql`${table.source} IN ('link', 'manager')`,
    ),
    check(
      "staff_time_entries_order_check",
      sql`${table.clock_out} IS NULL OR ${table.clock_out} > ${table.clock_in}`,
    ),
  ],
);

// Outcome of each print job a POS device sends straight to a printer (ePOS over
// the LAN). The server creates an 'expected' row when it hands a kitchen ticket
// to a device; the device's best-effort reports move it on. Nothing reads it on
// the print path. Cloud (Server Direct Print) jobs stay in order_print_statuses.
export const print_job_statuses = pgTable(
  "print_job_statuses",
  {
    // A kitchen ticket's PrintDoc.docId (shared by server and device), or a
    // device-generated id for other prints.
    job_id: text().primaryKey().notNull(),
    restaurant_id: integer().notNull(),
    printer_id: integer(),
    order_id: integer(),
    payment_id: integer(),
    // Only kitchenTicket today: receipts (taxReceipt) aren't tracked.
    doc_type: text().notNull(),
    // PrintDoc.docId, e.g. "type/kitchen/order/123/printer/4/time/…".
    doc_id: text(),
    user_id: integer(),
    device_id: text(),
    // What the reporting device runs: web build (release) plus the native
    // app's lemmonEnv (model, os, app version/build, OTA). Null for old clients.
    client_info: jsonb(),
    // Server time each step first reached the server. sending_at is often null:
    // when the printer answers within a second the device only reports the result.
    sending_at: timestamp({ withTimezone: true, mode: "string" }),
    printed_at: timestamp({ withTimezone: true, mode: "string" }),
    failed_at: timestamp({ withTimezone: true, mode: "string" }),
    // expected (server) -> sending -> printed | failed; printed is final.
    status: text().notNull(),
    error_message: text(),
    // The printer's own verdict from the ePOS response body (success / code attributes).
    printer_success: boolean(),
    printer_code: text(),
    duration_ms: integer(),
    client_started_at: timestamp({ withTimezone: true, mode: "string" }),
    completed_at: timestamp({ withTimezone: true, mode: "string" }),
    created_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    updated_at: timestamp({ withTimezone: true, mode: "string" })
      .defaultNow()
      .notNull(),
    // Minted with the row; holding it authorises reporting on this one job
    // without a session (POST /api/print-jobs/reports, from the native app).
    report_id: uuid().defaultRandom(),
  },
  (table) => [
    uniqueIndex("print_job_statuses_report_id_idx").using(
      "btree",
      table.report_id.asc().nullsLast().op("uuid_ops"),
    ),
    index("print_job_statuses_restaurant_created_idx").using(
      "btree",
      table.restaurant_id.asc().nullsLast().op("int4_ops"),
      table.created_at.asc().nullsLast().op("timestamptz_ops"),
    ),
    index("print_job_statuses_order_id_idx")
      .using("btree", table.order_id.asc().nullsLast().op("int4_ops"))
      .where(sql`${table.order_id} IS NOT NULL`),
    check(
      "print_job_statuses_status_check",
      sql`${table.status} IN ('expected', 'sending', 'printed', 'failed')`,
    ),
  ],
);
